/* eslint-disable no-restricted-globals */
// ╔════════════════════════════════════════════════════════════════════════════╗
// ║  COMPLETEGAMEADMINPANEL.JS - VERSION: 2025.12.27.0200                      ║
// ╚════════════════════════════════════════════════════════════════════════════╝
// ============================================================================
// 📦 VERSION: 2025.12.27.0200
// ============================================================================
// CHANGELOG:
// 2025.12.27.0200 - Statistics dashboard now fetches REAL data from API endpoints
// 2025.12.27.0145 - Added Admin Users tab + Statistics dashboard with graphs
// 2025.12.23.0210 - Added debug logging to question save, game activation validation
// 2025.12.23.0200 - Added validation: games need 1+ picture & 4+ questions to activate
// 2025.12.18.2145 - Parent quiz and display order fields for grouping (verified)
// 2025.12.15.2235 - FIX: Show error alert when files rejected before upload
// 2025.12.15.2230 - Added debug logging to picture upload
// 2025.12.15.2220 - Added bulk picture upload (select multiple files at once)
// 2025.12.15.2200 - FIX: Bulk upload ignores N/A variants when checking duplicates
// 2025.12.14.1855 - Added picture count next to question count in Game Modes list
// 2025.12.14.1815 - FIX: onDeleteGame now passes game_id instead of game object
// 2025.12.14.1800 - Added "Skip Duplicate Checking" toggle for bulk uploads
// 2025.12.14.1740 - Admin reply now sends notification to player via API
// 2025.12.14.1630 - FIX: Use admin_token (not adminToken) for API calls
// 2025.12.14.1620 - Uses /admin/rewards endpoint (not /rewards)
// 2025.12.14.1615 - FIX: Challenge approve/decline now calls API directly
// 2025.12.14.1615 - FIX: Creates claimable reward (50 TRAKs) for approved challenges
// 2025.12.14.1615 - FIX: Buttons disabled after sending (prevents double-click)
// 2025.12.14.1145 - Added Delete All Questions button per quiz
// 2025.12.14.1145 - Added onDeleteAllQuestions prop
// 2025.12.13.1345 - FIX: Search keywords now uses snake_case (search_keywords) for database
// 2025.12.12.1900 - Added App Settings screen (Terms, Privacy, GDPR URLs)
// 2025.12.12.1845 - Improved bulk upload: similarity checker with override checkboxes
// 2025.12.12.1845 - 25 char limit for game name, RED warning when exceeded
// 2025.12.03.0001 - Added 35 char limit to game name field (header display limit)
// 2025.12.02.1730 - Challenges now use snake_case to match database
// 2025.12.02.1545 - Added console.log debug for Edit button
// 2025.12.02.1535 - Grid message now per-game, ad URLs use snake_case for database
// 2025.12.02.1510 - Fixed wrong answer column names (wrong_answer1 not wrong_answer_1)
// 2025.12.02.1330 - Fixed picture display to use image_url from database
// 2025.12.01.0430 - Bulk upload format: 5 fields (question + 4 answers)
// 2025.12.01.0420 - Added userRole prop for role-based button visibility
// 2025.11.30.0140 - Description 45 char limit with counter and info tips
// 2025.11.29.0135 - Info tips for Active/Featured buttons, Ad locations guide
// 2025.11.29.0130 - Exit Ad fields: countdown, max impressions, date range
// 2025.11.29.0120 - Exit Ad & Interstitial Ad URL fields added
// 2025.11.29.0025 - Search Keywords field for quizzes
// 2025.11.29.0015 - Featured Quiz button, Featured Image checkbox for pictures
// 2025.11.28.XXXX - Separate Desktop (728x90) & Mobile (320x50) banners
// ============================================================================
import React, { useState, useEffect } from 'react';
import { Settings, Upload, FileText, Image, Trash2, Plus, ChevronDown, ChevronUp, Search, Edit, X, Check, AlertTriangle, Flag, Mail, Gift, MessageSquare, Award, Calendar, Eye, Info } from 'lucide-react';

// API URL for challenge updates
const API_URL = 'https://api.traktag.com/api';

// Simple Tooltip Component
const InfoTooltip = ({ text }) => {
  const [show, setShow] = useState(false);
  return (
    <div className="relative inline-block ml-2">
      <Info 
        size={16} 
        className="text-blue-300 cursor-help inline-block"
        onMouseEnter={() => setShow(true)}
        onMouseLeave={() => setShow(false)}
        onClick={() => setShow(!show)}
      />
      {show && (
        <div className="absolute z-50 left-6 top-0 bg-gray-900 text-white text-xs rounded-lg p-3 shadow-xl border border-gray-700 w-64">
          {text}
          <div className="absolute left-0 top-2 w-0 h-0 border-t-4 border-t-transparent border-b-4 border-b-transparent border-r-4 border-r-gray-900 -ml-1"></div>
        </div>
      )}
    </div>
  );
};

// ============================================================================
// ENHANCED COMPLETE GAME ADMIN PANEL - V6 WITH PICTURE MANAGEMENT
// ============================================================================
function CompleteGameAdminPanel({
  games,
  pictures,
  questions,
  challenges,
  messages,
  rewards,
  messageTemplates,
  onCreateGame,
  onEditGame,
  onDeleteGame,
  onToggleGameStatus,
  onSetFeaturedGame,
  onSelectGame,
  onUploadPicture,
  onDeletePicture,
  onMovePicture,
  onUpdatePicture, // 🆕 NEW: Update picture metadata
  onAddQuestion,
  onEditQuestion,
  onDeleteQuestion,
  onDeleteAllQuestions, // 🆕 NEW: Delete all questions for a game
  onToggleQuestionStatus,
  onBulkUpload,
  onApproveChallenge,
  onDeclineChallenge,
  onSendMessage,
  onAwardReward,
  onUpdateTemplate,
  players,
  editingGame,
  editGameData,
  setEditGameData,
  onSaveEditGame,
  onCancelEditGame,
  // Picture Grid Settings
  pictureGridSettings,
  onUpdatePictureGridSettings,
  // Role-based access
  userRole = 'admin' // 'super_admin', 'admin', or 'moderator'
}) {
  // Check if user can edit (not a moderator)
  const canEdit = userRole !== 'moderator';
  
  const [currentScreen, setCurrentScreen] = useState('games');
  const [selectedGameId, setSelectedGameId] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState('');
  const [modalData, setModalData] = useState(null);
  
  // 🆕 NEW: Load player messages directly from localStorage
  const [playerMessages, setPlayerMessages] = useState([]);
  
  // 🆕 NEW: Reply functionality state
  const [replyingTo, setReplyingTo] = useState(null);
  const [replyText, setReplyText] = useState('');
  
  // 🆕 NEW: App Settings state (Terms, Privacy, GDPR URLs)
  const [appSettings, setAppSettings] = useState(() => {
    try {
      const stored = localStorage.getItem('trakquiz_app_settings');
      if (stored) return JSON.parse(stored);
    } catch (e) {}
    return {
      termsUrl: 'https://traktag.com/terms',
      privacyUrl: 'https://traktag.com/privacy',
      gdprUrl: 'https://traktag.com/gdpr',
      supportEmail: 'support@traktag.com',
      appName: 'TRAKtag Quiz',
      copyrightYear: '2025'
    };
  });
  
  // Save app settings to localStorage when changed
  const saveAppSettings = (newSettings) => {
    setAppSettings(newSettings);
    localStorage.setItem('trakquiz_app_settings', JSON.stringify(newSettings));
  };
  useEffect(() => {
    const loadPlayerMessages = () => {
      try {
        const stored = localStorage.getItem('playerMessages');
        if (stored) {
          const parsed = JSON.parse(stored);
          setPlayerMessages(parsed);
          console.log('📨 Loaded', parsed.length, 'player messages');
        }
      } catch (error) {
        console.error('Error loading player messages:', error);
      }
    };
    
    // Load immediately
    loadPlayerMessages();
    
    // Refresh every 5 seconds to catch new messages
    const interval = setInterval(loadPlayerMessages, 5000);
    return () => clearInterval(interval);
  }, []);
  
  // Use playerMessages if available, otherwise fall back to messages prop
  const displayMessages = playerMessages.length > 0 ? playerMessages : (messages || []);
  
  // 🆕 NEW: Extract unique players from messages for recipient dropdown
  const uniquePlayers = [...new Set(playerMessages.map(msg => ({
    id: msg.playerId,
    name: msg.from,
    playerId: msg.playerId
  })))].reduce((acc, player) => {
    if (!acc.find(p => p.playerId === player.playerId)) {
      acc.push(player);
    }
    return acc;
  }, []);
  
  // 🆕 NEW: Handle admin reply to player
  const handleSendReply = async (originalMessage) => {
    if (!replyText.trim()) {
      alert('Please enter a reply message');
      return;
    }
    
    const adminReply = {
      id: Date.now(),
      type: 'admin_to_player',
      originalMessageId: originalMessage.id,
      to: originalMessage.from,
      toPlayerId: originalMessage.playerId,
      subject: `Re: ${originalMessage.subject}`,
      message: replyText,
      originalMessage: originalMessage.message,
      game: originalMessage.game,
      timestamp: new Date().toISOString(),
      read: false,
      from: 'Game Admin'
    };
    
    // Save to adminMessages in localStorage (for admin's sent box)
    const existingAdminMessages = JSON.parse(localStorage.getItem('adminMessages') || '[]');
    existingAdminMessages.unshift(adminReply);
    localStorage.setItem('adminMessages', JSON.stringify(existingAdminMessages));
    
    // 📨 Also send notification to player via API
    if (originalMessage.playerId) {
      try {
        const token = localStorage.getItem('admin_token');
        await fetch(`${API_URL}/admin/notifications`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            player_id: originalMessage.playerId,
            type: 'admin_message',
            subject: `Re: ${originalMessage.subject}`,
            message: replyText,
            metadata: {
              original_message_id: originalMessage.id,
              game: originalMessage.game
            }
          })
        });
        console.log('📨 Notification sent to player via API');
      } catch (err) {
        console.error('❌ Failed to send notification via API:', err);
      }
    }
    
    // Mark original message as read
    const updatedPlayerMessages = playerMessages.map(msg => 
      msg.id === originalMessage.id ? { ...msg, read: true, replied: true } : msg
    );
    setPlayerMessages(updatedPlayerMessages);
    localStorage.setItem('playerMessages', JSON.stringify(updatedPlayerMessages));
    
    console.log('✅ Admin reply sent to player:', originalMessage.from);
    
    // Reset reply state
    setReplyingTo(null);
    setReplyText('');
    alert('Reply sent to player!');
  };
  
  const [bulkUploadText, setBulkUploadText] = useState('');
  const [bulkUploadErrors, setBulkUploadErrors] = useState([]);
  // 🆕 NEW: Similarity checking state
  const [parsedQuestions, setParsedQuestions] = useState([]);
  const [flaggedQuestions, setFlaggedQuestions] = useState([]);
  const [overriddenFlags, setOverriddenFlags] = useState(new Set());
  const [validationComplete, setValidationComplete] = useState(false);
  const [skipValidation, setSkipValidation] = useState(false); // 🆕 Skip similarity check toggle
  const [questionFilter, setQuestionFilter] = useState('all');
  const [questionSearch, setQuestionSearch] = useState('');
  const [editingQuestionId, setEditingQuestionId] = useState(null);
  const [editingQuestionData, setEditingQuestionData] = useState(null);
  const [creatingGame, setCreatingGame] = useState(false);
  const [newGameData, setNewGameData] = useState({ 
    name: '', 
    description: '', 
    groupQuizAdFrequency: 5, 
    pictureMode: 'picture-on-black',  // 🔧 HARDCODED: Always picture-on-black
    desktopBannerUrl: '',
    mobileBannerUrl: '',
    staticAdUrl: '',
    exitAdUrl: '',
    exitAdCountdown: 5,
    exitAdMaxImpressions: null,
    exitAdStartDate: '',
    exitAdEndDate: '',
    interstitialAdUrl: '',
    adClickUrl: '',
    parent_id: null,
    display_order: 0
  });
  const [composingMessage, setComposingMessage] = useState(false);
  const [newMessageData, setNewMessageData] = useState({ to: '', subject: '', message: '' });
  const [challengeButtonStates, setChallengeButtonStates] = useState({});
  
  // 🆕 NEW: Picture editing state
  const [editingPictureId, setEditingPictureId] = useState(null);
  const [editingPictureData, setEditingPictureData] = useState(null);

  const selectedGame = games.find(g => g.game_id === selectedGameId);
  const gamePictures = pictures.filter(p => p.game_id === selectedGameId).sort((a, b) => a.display_order - b.display_order);
  const gameQuestions = questions.filter(q => q.game_id === selectedGameId);
  
  const filteredQuestions = gameQuestions.filter(q => {
    const matchesFilter = questionFilter === 'all' || 
      (questionFilter === 'active' && q.active) ||
      (questionFilter === 'inactive' && !q.active) ||
      (questionFilter === 'reported' && q.reported);
    
    const matchesSearch = questionSearch === '' || 
      q.question_text.toLowerCase().includes(questionSearch.toLowerCase());
    
    return matchesFilter && matchesSearch;
  });

  // 🆕 NEW: Handle picture file upload (supports multiple files)
  const handlePictureUpload = (e, gameId) => {
    console.log('📸 handlePictureUpload called', { gameId, files: e.target.files });
    
    const files = Array.from(e.target.files);
    if (!files.length) {
      console.log('📸 No files selected');
      return;
    }

    console.log(`📸 Processing ${files.length} file(s)`);

    // Count current pictures for this game
    const currentCount = pictures.filter(p => String(p.game_id) === String(gameId)).length;
    const maxPictures = 20;
    const availableSlots = maxPictures - currentCount;
    
    if (files.length > availableSlots) {
      alert(`Only ${availableSlots} picture slots available. You selected ${files.length} files.`);
      return;
    }

    let uploadedCount = 0;
    let errorCount = 0;
    let pendingReaders = 0;
    const errors = [];

    files.forEach((file, index) => {
      console.log(`📸 Processing file ${index + 1}: ${file.name} (${file.type}, ${file.size} bytes)`);
      
      // Validate file type
      if (!file.type.startsWith('image/')) {
        errors.push(`${file.name}: Not an image file`);
        errorCount++;
        return;
      }

      // Validate file size (500KB max)
      const maxSize = 500 * 1024; // 500KB in bytes
      if (file.size > maxSize) {
        errors.push(`${file.name}: Too large (${(file.size / 1024).toFixed(0)}KB > 500KB limit)`);
        errorCount++;
        return;
      }

      // Convert to base64
      pendingReaders++;
      const reader = new FileReader();
      reader.onloadend = () => {
        console.log(`📸 FileReader complete for ${file.name}, calling onUploadPicture`);
        
        const base64String = reader.result;
        if (onUploadPicture) {
          onUploadPicture(gameId, {
            file_name: file.name,
            file_path: base64String,
            file_size: file.size,
            // Default metadata
            start_date: null,
            end_date: null,
            max_impressions: null,
            current_impressions: 0,
            active: true
          });
        } else {
          console.error('📸 ERROR: onUploadPicture prop is not defined!');
        }
        uploadedCount++;
        pendingReaders--;
        
        // Show summary after all files processed
        if (pendingReaders === 0 && errorCount > 0) {
          alert(`Uploaded ${uploadedCount} picture(s).\n\nErrors (${errorCount}):\n${errors.join('\n')}`);
        }
      };
      reader.onerror = () => {
        console.error(`📸 FileReader error for ${file.name}`, reader.error);
        pendingReaders--;
      };
      reader.readAsDataURL(file);
    });
    
    // If ALL files were rejected before FileReader stage, show errors immediately
    if (pendingReaders === 0 && errorCount > 0) {
      alert(`Upload failed:\n\n${errors.join('\n')}`);
    }
    
    // Reset the input so the same files can be selected again
    e.target.value = '';
  };

  // 🆕 NEW: Check if picture is currently active
  const isPictureActive = (picture) => {
    if (!picture.active) return false;
    
    const now = new Date();
    
    // Check date range
    if (picture.start_date && new Date(picture.start_date) > now) return false;
    if (picture.end_date && new Date(picture.end_date) < now) return false;
    
    // Check impression limit
    if (picture.max_impressions && picture.current_impressions >= picture.max_impressions) return false;
    
    return true;
  };

  // 🆕 NEW: Calculate similarity between two strings (0-1, higher = more similar)
  const calculateSimilarity = (str1, str2) => {
    const s1 = str1.toLowerCase().replace(/[^a-z0-9\s]/g, '').trim();
    const s2 = str2.toLowerCase().replace(/[^a-z0-9\s]/g, '').trim();
    
    if (s1 === s2) return 1;
    if (s1.length === 0 || s2.length === 0) return 0;
    
    // Check if one contains the other
    if (s1.includes(s2) || s2.includes(s1)) return 0.9;
    
    // Word overlap check
    const words1 = new Set(s1.split(/\s+/).filter(w => w.length > 2));
    const words2 = new Set(s2.split(/\s+/).filter(w => w.length > 2));
    const intersection = [...words1].filter(w => words2.has(w));
    const union = new Set([...words1, ...words2]);
    
    if (union.size === 0) return 0;
    return intersection.length / union.size;
  };

  // 🆕 NEW: Check for similar questions in existing database
  const findSimilarQuestions = (newQuestion, existingQuestions, threshold = 0.6) => {
    const similar = [];
    for (const existing of existingQuestions) {
      const similarity = calculateSimilarity(newQuestion, existing.question_text);
      if (similarity >= threshold) {
        similar.push({ question: existing.question_text, similarity: Math.round(similarity * 100) });
      }
    }
    return similar;
  };

  const handleBulkUploadValidate = () => {
    if (!selectedGameId) {
      alert('Please select a game first');
      return;
    }
    if (!bulkUploadText.trim()) {
      alert('Please paste questions');
      return;
    }
    
    const allLines = bulkUploadText.trim().split('\n');
    const errors = [];
    const parsed = [];
    const flagged = [];
    
    // Get existing questions for this game
    const existingQuestions = questions.filter(q => q.game_id === selectedGameId);
    
    let actualLineNumber = 0;
    allLines.forEach((line) => {
      actualLineNumber++;
      if (!line.trim()) return;
      
      const parts = line.split(';').map(p => p.trim());
      
      // Accept 5 fields (question + 4 answers) or 6 fields (+ explanation)
      if (parts.length < 5) {
        errors.push({ 
          lineNumber: actualLineNumber, 
          message: `Expected at least 5 parts separated by semicolons (found ${parts.length})`, 
          line 
        });
        return;
      }
      
      const [question, correct, wrong1, wrong2, wrong3, explanation] = parts;
      
      if (!question || !correct || !wrong1 || !wrong2 || !wrong3) {
        errors.push({ 
          lineNumber: actualLineNumber, 
          message: 'Missing required fields', 
          line 
        });
        return;
      }
      
      // 🔧 FIX: Helper to check if answer is N/A variant
      const isNA = (val) => val && val.toLowerCase().replace(/[^a-z]/g, '') === 'na';
      
      // 🔧 FIX: Filter out N/A variants before checking for duplicates
      const answers = [correct, wrong1, wrong2, wrong3];
      const nonNAAnswers = answers.filter(a => !isNA(a));
      
      // Check only non-N/A answers for duplicates
      if (new Set(nonNAAnswers).size !== nonNAAnswers.length) {
        errors.push({ 
          lineNumber: actualLineNumber, 
          message: 'Non-N/A answers must be different', 
          line 
        });
        return;
      }
      
      const questionData = {
        lineNumber: actualLineNumber,
        question_text: question,
        correct_answer: correct,
        wrong_answer1: wrong1,
        wrong_answer2: wrong2,
        wrong_answer3: wrong3,
        explanation: explanation || null
      };
      
      // 🆕 Skip similarity check if toggle is on
      if (skipValidation) {
        parsed.push(questionData);
      } else {
        // Check for similar existing questions
        const similarTo = findSimilarQuestions(question, existingQuestions);
        
        // Also check against other new questions in this batch
        const similarInBatch = parsed.filter(p => calculateSimilarity(question, p.question_text) >= 0.6)
          .map(p => ({ question: p.question_text, similarity: Math.round(calculateSimilarity(question, p.question_text) * 100) }));
        
        const allSimilar = [...similarTo, ...similarInBatch];
        
        if (allSimilar.length > 0) {
          flagged.push({
            ...questionData,
            similarTo: allSimilar
          });
        } else {
          parsed.push(questionData);
        }
      }
    });
    
    setBulkUploadErrors(errors);
    setParsedQuestions(parsed);
    setFlaggedQuestions(flagged);
    setOverriddenFlags(new Set());
    setValidationComplete(true);
    
    if (errors.length > 0) {
      // Don't proceed if there are format errors
      return;
    }
  };

  // 🆕 NEW: Toggle override for flagged question
  const toggleOverride = (lineNumber) => {
    setOverriddenFlags(prev => {
      const newSet = new Set(prev);
      if (newSet.has(lineNumber)) {
        newSet.delete(lineNumber);
      } else {
        newSet.add(lineNumber);
      }
      return newSet;
    });
  };

  // 🆕 NEW: Handle actual upload
  const handleBulkUploadConfirm = (includeOverridden = false) => {
    const questionsToUpload = [...parsedQuestions];
    
    if (includeOverridden) {
      const overriddenQuestions = flaggedQuestions.filter(q => overriddenFlags.has(q.lineNumber));
      questionsToUpload.push(...overriddenQuestions);
    }
    
    if (questionsToUpload.length === 0) {
      alert('No questions to upload');
      return;
    }
    
    // Remove lineNumber before sending to server
    const cleanQuestions = questionsToUpload.map(({ lineNumber, similarTo, ...rest }) => rest);
    
    onBulkUpload(selectedGameId, cleanQuestions);
    
    // Reset state
    setBulkUploadText('');
    setBulkUploadErrors([]);
    setParsedQuestions([]);
    setFlaggedQuestions([]);
    setOverriddenFlags(new Set());
    setValidationComplete(false);
    
    alert(`✅ Uploaded ${cleanQuestions.length} question${cleanQuestions.length === 1 ? '' : 's'} to ${selectedGame.name}!`);
  };

  // 🆕 NEW: Reset validation state
  const resetBulkUpload = () => {
    setBulkUploadText('');
    setBulkUploadErrors([]);
    setParsedQuestions([]);
    setFlaggedQuestions([]);
    setOverriddenFlags(new Set());
    setValidationComplete(false);
  };

  const ScreenNav = ({ icon: Icon, label, screen, badge }) => (
    <button
      onClick={() => setCurrentScreen(screen)}
      className={`flex items-center gap-2 px-4 py-2 rounded text-sm font-bold transition-colors relative ${
        currentScreen === screen 
          ? 'bg-purple-600 text-white' 
          : 'bg-purple-700 text-purple-200 hover:bg-purple-600 hover:text-white'
      }`}
    >
      <Icon size={16} />
      {label}
      {badge > 0 && (
        <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
          {badge}
        </span>
      )}
    </button>
  );

  return (
    <div className="bg-purple-900 rounded-lg p-4">
      <h3 className="text-white font-bold text-xl mb-4 flex items-center gap-2">
        <Settings size={24} />
        Complete Game Admin System
      </h3>

      {/* Navigation */}
      <div className="mb-4 space-y-2">
        <div className="text-purple-300 text-xs font-bold uppercase">Content Management</div>
        <div className="flex flex-wrap gap-2">
          <ScreenNav icon={FileText} label="Games" screen="games" badge={games.length} />
          <ScreenNav icon={Image} label="Pictures" screen="pictures" badge={0} />
          <ScreenNav icon={FileText} label="Questions" screen="questions" badge={0} />
          <ScreenNav icon={Upload} label="Bulk Upload" screen="bulk" badge={0} />
          <ScreenNav icon={Image} label="Picture Grid" screen="picturegrid" badge={0} />
        </div>
        
        <div className="text-purple-300 text-xs font-bold uppercase mt-3">Communication</div>
        <div className="flex flex-wrap gap-2">
          <ScreenNav icon={Flag} label="Challenges" screen="challenges" badge={challenges.filter(c => c.status === 'pending').length} />
          <ScreenNav icon={MessageSquare} label="Messages" screen="messages" badge={displayMessages.filter(m => !m.read && m.type !== 'sent').length} />
          <ScreenNav icon={Gift} label="Rewards" screen="rewards" badge={0} />
        </div>
        
        <div className="text-purple-300 text-xs font-bold uppercase mt-3">Configuration</div>
        <div className="flex flex-wrap gap-2">
          <ScreenNav icon={Settings} label="App Settings" screen="appsettings" badge={0} />
          {canEdit && <ScreenNav icon={Award} label="Admin Users" screen="adminusers" badge={0} />}
        </div>
        
        <div className="text-purple-300 text-xs font-bold uppercase mt-3">Analytics</div>
        <div className="flex flex-wrap gap-2">
          <ScreenNav icon={Eye} label="Statistics" screen="statistics" badge={0} />
        </div>
      </div>

      {/* GAMES SCREEN */}
      {currentScreen === 'games' && (
        <div className="bg-purple-800 rounded-lg p-4 space-y-4">
          <div className="flex justify-between items-center">
            <h4 className="text-white font-bold">
              🎮 Game Modes ({games.length})
              <InfoTooltip text="Create as many quiz games as you want! Each game can have its own questions, pictures, and settings. Perfect for different topics, difficulty levels, or themed quizzes." />
            </h4>
            {canEdit && (
            <button
              onClick={() => {
                setCreatingGame(true);
                setNewGameData({ name: '', description: '', groupQuizAdFrequency: 5, pictureMode: 'picture-on-black' });
              }}
              className="bg-purple-600 hover:bg-purple-500 text-white text-xs px-3 py-2 rounded flex items-center gap-1"
            >
              <Plus size={14} />
              Create Game
            </button>
            )}
          </div>

          {creatingGame && canEdit && (
            <div className="bg-purple-700 rounded-lg p-4 space-y-3">
              <h5 className="text-white font-bold">Create New Game</h5>
              <div>
                <label className="text-purple-200 text-xs block mb-1">
                  Game Name:
                  <span className={`ml-2 ${(newGameData.name?.length || 0) > 25 ? 'text-red-400 font-bold' : (newGameData.name?.length || 0) > 20 ? 'text-yellow-400' : 'text-purple-400'}`}>
                    ({newGameData.name?.length || 0}/25 chars)
                    {(newGameData.name?.length || 0) > 25 && ' ⚠️ TOO LONG!'}
                  </span>
                </label>
                <input
                  type="text"
                  value={newGameData.name}
                  onChange={(e) => setNewGameData({...newGameData, name: e.target.value})}
                  placeholder="Enter game name (max 25 chars)..."
                  className={`w-full px-3 py-2 rounded text-sm ${(newGameData.name?.length || 0) > 25 ? 'bg-red-900 text-red-200 border-2 border-red-500' : 'bg-purple-600 text-white'}`}
                />
                {(newGameData.name?.length || 0) > 25 && (
                  <p className="text-red-400 text-xs mt-1">⚠️ Name too long - will be truncated with "..." on home screen</p>
                )}
              </div>
              <div>
                <label className="text-purple-200 text-xs block mb-1">
                  Description: 
                  <span className={`ml-2 ${(newGameData.description?.length || 0) > 40 ? 'text-yellow-400' : 'text-purple-400'}`}>
                    ({newGameData.description?.length || 0}/45 chars)
                  </span>
                </label>
                <input
                  type="text"
                  value={newGameData.description}
                  onChange={(e) => setNewGameData({...newGameData, description: e.target.value.slice(0, 45)})}
                  placeholder="Brief description (max 45 chars)"
                  maxLength={45}
                  className="w-full bg-purple-600 text-white px-3 py-2 rounded text-sm"
                />
                <p className="text-purple-400 text-xs mt-1 italic">💡 Max 45 characters - this displays in quiz cards on Home and Quizzes pages. Longer text gets truncated with "..."</p>
              </div>
              <div>
                <label className="text-purple-200 text-xs block mb-1">🔍 Search Keywords:</label>
                <input
                  type="text"
                  value={newGameData.search_keywords || ''}
                  onChange={(e) => setNewGameData({...newGameData, search_keywords: e.target.value})}
                  placeholder="e.g. cakes, pies, flour, ovens, Mrs Beeton"
                  className="w-full bg-purple-600 text-white px-3 py-2 rounded text-sm"
                />
                <p className="text-purple-300 text-xs mt-1">Separate search keywords with a comma</p>
              </div>
              
              {/* 🆕 Parent Quiz Selection */}
              <div>
                <label className="text-purple-200 text-xs block mb-1">
                  Parent Quiz (for sub-sections):
                  <InfoTooltip text="If this quiz is a section of a larger quiz, select the parent. For example, 'Steam Engines' could be a child of 'Trainspotting'." />
                </label>
                <select
                  value={newGameData.parent_id || ''}
                  onChange={(e) => setNewGameData({...newGameData, parent_id: e.target.value ? Number(e.target.value) : null})}
                  className="w-full bg-purple-600 text-white px-3 py-2 rounded text-sm"
                >
                  <option value="">None (standalone quiz)</option>
                  {games.filter(g => !g.parent_id).map(g => (
                    <option key={g.game_id} value={g.game_id}>{g.name}</option>
                  ))}
                </select>
              </div>
              
              {/* 🆕 Display Order */}
              <div>
                <label className="text-purple-200 text-xs block mb-1">
                  Display Order:
                  <InfoTooltip text="Controls the order this quiz appears. Lower numbers appear first. Use this to sort sections within a parent quiz." />
                </label>
                <input
                  type="number"
                  min="0"
                  value={newGameData.display_order || 0}
                  onChange={(e) => setNewGameData({...newGameData, display_order: Number(e.target.value)})}
                  className="w-full bg-purple-600 text-white px-3 py-2 rounded text-sm"
                />
              </div>
              
              <div>
                <label className="text-purple-200 text-xs block mb-1">
                  Group Quiz Ad Frequency:
                  <InfoTooltip text="In Group Quiz mode (Zero-Timer for quiz masters), this controls how often ads appear. Set to 0 for no ads, or choose a number to show ads every N questions. Ads won't interrupt the quiz master's flow!" />
                </label>
                <select
                  value={newGameData.groupQuizAdFrequency || 5}
                  onChange={(e) => setNewGameData({...newGameData, groupQuizAdFrequency: Number(e.target.value)})}
                  className="w-full bg-purple-600 text-white px-3 py-2 rounded text-sm"
                >
                  <option value={0}>0 - No ads</option>
                  <option value={1}>1 - Every question</option>
                  <option value={3}>3 - Every 3 questions</option>
                  <option value={5}>5 - Every 5 questions (default)</option>
                  <option value={10}>10 - Every 10 questions</option>
                  <option value={15}>15 - Every 15 questions</option>
                  <option value={20}>20 - Every 20 questions</option>
                </select>
                <p className="text-purple-300 text-xs mt-1">Interstitial ads shown during countdown (Group Quiz Mode only)</p>
              </div>
              {/* 🔧 Display Mode removed - hardcoded to picture-on-black */}
              
              {/* 📺 Advertisement URLs Section */}
              <div className="border-t border-purple-600 pt-4 mt-4">
                <h5 className="text-white font-bold mb-2">📺 Advertisement URLs</h5>
                <p className="text-purple-200 text-xs mb-2">
                  Paste full URLs from Google Ads, Facebook Ads, or your ad provider
                </p>
                
                {/* Ad Locations Guide */}
                <div className="bg-purple-900 rounded-lg p-3 mb-4 text-xs">
                  <p className="text-yellow-300 font-bold mb-2">📍 AD PLACEMENT LOCATIONS:</p>
                  <div className="grid grid-cols-2 gap-2 text-purple-200">
                    <div>🖥️ <strong>Desktop Banner</strong><br/>→ Bottom of Admin screens (wide screens)</div>
                    <div>📱 <strong>Mobile Banner</strong><br/>→ Bottom of game screen (phones)</div>
                    <div>📺 <strong>Static Ad</strong><br/>→ Quiz screen during 0s/10s/15s modes</div>
                    <div>🚪 <strong>Exit Ad</strong><br/>→ Full screen when exiting quiz</div>
                    <div>🎬 <strong>Interstitial</strong><br/>→ Full screen during 25s/35s countdown</div>
                    <div>🔗 <strong>Click URL</strong><br/>→ Where all ads link to when tapped</div>
                  </div>
                </div>
                
                {/* Desktop Banner Ad URL */}
                <div className="mb-3">
                  <label className="text-purple-200 text-xs block mb-1 font-semibold">
                    🖥️ Desktop Banner URL (Game Admin bottom - laptop/desktop view):
                  </label>
                  <input
                    type="url"
                    value={newGameData.desktopBannerUrl || ''}
                    onChange={(e) => setNewGameData({...newGameData, desktopBannerUrl: e.target.value})}
                    placeholder="https://via.placeholder.com/728x90?text=Desktop+Banner"
                    className="w-full bg-purple-600 text-white px-3 py-2 rounded text-sm"
                  />
                  <p className="text-purple-300 text-xs mt-1">
                    <strong>Size: 728×90 pixels</strong> (Leaderboard) - Perfect for wide screens
                  </p>
                  <p className="text-purple-400 text-xs mt-1 italic">
                    Test: https://via.placeholder.com/728x90/4A5568/FFFFFF?text=Desktop+Banner
                  </p>
                </div>
                
                {/* Mobile Banner Ad URL */}
                <div className="mb-3">
                  <label className="text-purple-200 text-xs block mb-1 font-semibold">
                    📱 Mobile Banner URL (Player Admin bottom - phone/tablet view):
                  </label>
                  <input
                    type="url"
                    value={newGameData.mobileBannerUrl || ''}
                    onChange={(e) => setNewGameData({...newGameData, mobileBannerUrl: e.target.value})}
                    placeholder="https://via.placeholder.com/320x50?text=Mobile+Banner"
                    className="w-full bg-purple-600 text-white px-3 py-2 rounded text-sm"
                  />
                  <p className="text-purple-300 text-xs mt-1">
                    <strong>Size: 320×50 pixels</strong> (Mobile Banner) - Perfect for phones
                  </p>
                  <p className="text-purple-400 text-xs mt-1 italic">
                    Test: https://via.placeholder.com/320x50/4A5568/FFFFFF?text=Mobile+Banner
                  </p>
                </div>
                
                {/* Static Ad URL */}
                <div className="mb-3">
                  <label className="text-purple-200 text-xs block mb-1 font-semibold">
                    📺 Static Ad URL (300×250 - Quiz screens during gameplay):
                  </label>
                  <input
                    type="url"
                    value={newGameData.staticAdUrl || ''}
                    onChange={(e) => setNewGameData({...newGameData, staticAdUrl: e.target.value})}
                    placeholder="https://via.placeholder.com/300x250?text=Static+Ad"
                    className="w-full bg-purple-600 text-white px-3 py-2 rounded text-sm"
                  />
                  <p className="text-purple-300 text-xs mt-1">
                    <strong>Size: 300×250</strong> - Used in Zero Timer mode & 10s/15s timed modes
                  </p>
                </div>
                
                {/* Exit Ad URL */}
                <div className="mb-3 bg-purple-800 p-3 rounded-lg">
                  <label className="text-purple-200 text-xs block mb-1 font-semibold">
                    🚪 Exit Ad URL (320×480 - Game Over/Exit screen):
                  </label>
                  <input
                    type="url"
                    value={newGameData.exitAdUrl || ''}
                    onChange={(e) => setNewGameData({...newGameData, exitAdUrl: e.target.value})}
                    placeholder="https://via.placeholder.com/320x480?text=Exit+Ad"
                    className="w-full bg-purple-600 text-white px-3 py-2 rounded text-sm"
                  />
                  <p className="text-purple-300 text-xs mt-1">
                    <strong>Size: 320×480</strong> - Full screen ad shown when player exits quiz or game ends
                  </p>
                  
                  {/* Exit Ad Countdown & Impressions */}
                  <div className="mt-2 flex gap-2">
                    <div className="flex-1">
                      <label className="text-purple-300 text-xs">⏱️ Countdown (sec):</label>
                      <input
                        type="number"
                        min="0"
                        max="30"
                        value={newGameData.exitAdCountdown || 5}
                        onChange={(e) => setNewGameData({...newGameData, exitAdCountdown: Number(e.target.value)})}
                        className="w-full bg-purple-600 text-white px-2 py-1 rounded text-sm mt-1"
                      />
                      <p className="text-purple-400 text-xs mt-1 italic">
                        💡 Seconds before "Close" button appears
                      </p>
                    </div>
                    <div className="flex-1">
                      <label className="text-purple-300 text-xs">👁️ Max Impressions:</label>
                      <input
                        type="number"
                        min="0"
                        value={newGameData.exitAdMaxImpressions || ''}
                        onChange={(e) => setNewGameData({...newGameData, exitAdMaxImpressions: e.target.value ? Number(e.target.value) : null})}
                        placeholder="∞"
                        className="w-full bg-purple-600 text-white px-2 py-1 rounded text-sm mt-1"
                      />
                      <p className="text-purple-400 text-xs mt-1 italic">
                        💡 Ad auto-stops after X views
                      </p>
                    </div>
                  </div>
                  
                  {/* Exit Ad Date Range */}
                  <div className="mt-2 grid grid-cols-2 gap-2">
                    <div>
                      <label className="text-purple-300 text-xs">📅 Start Date:</label>
                      <input
                        type="date"
                        value={newGameData.exitAdStartDate || ''}
                        onChange={(e) => setNewGameData({...newGameData, exitAdStartDate: e.target.value})}
                        className="w-full bg-purple-600 text-white px-2 py-1 rounded text-sm mt-1"
                      />
                    </div>
                    <div>
                      <label className="text-purple-300 text-xs">📅 End Date:</label>
                      <input
                        type="date"
                        value={newGameData.exitAdEndDate || ''}
                        onChange={(e) => setNewGameData({...newGameData, exitAdEndDate: e.target.value})}
                        className="w-full bg-purple-600 text-white px-2 py-1 rounded text-sm mt-1"
                      />
                    </div>
                  </div>
                  <p className="text-purple-400 text-xs mt-1 italic">
                    💡 Schedule ad campaigns - leave blank to show always
                  </p>
                </div>
                
                {/* Interstitial Ad URL */}
                <div className="mb-3">
                  <label className="text-purple-200 text-xs block mb-1 font-semibold">
                    🎬 Interstitial Ad URL (320×480 - 25s/35s timer modes):
                  </label>
                  <input
                    type="url"
                    value={newGameData.interstitialAdUrl || ''}
                    onChange={(e) => setNewGameData({...newGameData, interstitialAdUrl: e.target.value})}
                    placeholder="https://via.placeholder.com/320x480?text=Interstitial+Ad"
                    className="w-full bg-purple-600 text-white px-3 py-2 rounded text-sm"
                  />
                  <p className="text-purple-300 text-xs mt-1">
                    <strong>Size: 320×480</strong> - Full screen ad for 25s/35s Group Quiz modes
                  </p>
                </div>
                
                {/* Ad Click URL */}
                <div className="mb-3">
                  <label className="text-purple-200 text-xs block mb-1 font-semibold">
                    🔗 Ad Click URL (destination when ads are clicked):
                  </label>
                  <input
                    type="url"
                    value={newGameData.adClickUrl || ''}
                    onChange={(e) => setNewGameData({...newGameData, adClickUrl: e.target.value})}
                    placeholder="https://sponsor-website.com"
                    className="w-full bg-purple-600 text-white px-3 py-2 rounded text-sm"
                  />
                  <p className="text-purple-300 text-xs mt-1">
                    <strong>All ads link here when clicked</strong> - Opens in new tab
                  </p>
                </div>
              </div>
              
              <div className="flex gap-2">
                <button
                  onClick={() => {
                    if (!newGameData.name.trim() || !newGameData.description.trim()) {
                      alert('Please fill in both name and description');
                      return;
                    }
                    // 🔧 FIX: Check for duplicate game name
                    const duplicateName = games.find(g => 
                      g.name.toLowerCase().trim() === newGameData.name.toLowerCase().trim()
                    );
                    if (duplicateName) {
                      alert(`A game named "${duplicateName.name}" already exists. Please use a different name.`);
                      return;
                    }
                    onCreateGame(newGameData);
                    setCreatingGame(false);
                    setNewGameData({ 
                      name: '', 
                      description: '', 
                      groupQuizAdFrequency: 5, 
                      pictureMode: 'picture-on-black',  // 🔧 HARDCODED
                      desktopBannerUrl: '',
                      mobileBannerUrl: '',
                      staticAdUrl: '',
                      exitAdUrl: '',
                      interstitialAdUrl: '',
                      adClickUrl: '',
                      parent_id: null,
                      display_order: 0
                    });
                  }}
                  className="flex-1 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded font-bold text-sm"
                >
                  Save
                </button>
                <button
                  onClick={() => {
                    setCreatingGame(false);
                    setNewGameData({ 
                      name: '', 
                      description: '', 
                      groupQuizAdFrequency: 5, 
                      pictureMode: 'picture-on-black',  // 🔧 HARDCODED
                      desktopBannerUrl: '',
                      mobileBannerUrl: '',
                      staticAdUrl: '',
                      adClickUrl: '',
                      parent_id: null,
                      display_order: 0
                    });
                  }}
                  className="flex-1 bg-purple-600 hover:bg-purple-500 text-white px-4 py-2 rounded text-sm"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          <div className="space-y-2">
            {games.map(game => (
              <div key={game.game_id} className="bg-purple-700 rounded-lg p-3">
                {editingGame === game.game_id ? (
                  <>
                    <div className="space-y-2 mb-2">
                      <div>
                        <div className="flex justify-between text-xs mb-1">
                          <span className="text-purple-300">Game Name (max 25 chars)</span>
                          <span className={`${(editGameData.name?.length || 0) > 25 ? 'text-red-400 font-bold' : (editGameData.name?.length || 0) > 20 ? 'text-yellow-400' : 'text-purple-400'}`}>
                            {editGameData.name?.length || 0}/25
                            {(editGameData.name?.length || 0) > 25 && ' ⚠️'}
                          </span>
                        </div>
                        <input
                          type="text"
                          value={editGameData.name}
                          onChange={(e) => setEditGameData({...editGameData, name: e.target.value})}
                          className={`w-full px-2 py-1 rounded text-sm ${(editGameData.name?.length || 0) > 25 ? 'bg-red-900 text-red-200 border-2 border-red-500' : 'bg-purple-600 text-white'}`}
                        />
                        {(editGameData.name?.length || 0) > 25 && (
                          <p className="text-red-400 text-xs mt-1">⚠️ Will be truncated with "..."</p>
                        )}
                      </div>
                      <div>
                        <div className="flex justify-between text-xs mb-1">
                          <span className="text-purple-300">Description (max 45 chars for quiz cards)</span>
                          <span className={editGameData.description?.length > 40 ? 'text-yellow-400' : 'text-purple-400'}>
                            {editGameData.description?.length || 0}/45
                          </span>
                        </div>
                        <input
                          type="text"
                          value={editGameData.description}
                          onChange={(e) => setEditGameData({...editGameData, description: e.target.value.slice(0, 45)})}
                          maxLength={45}
                          className="w-full bg-purple-600 text-white px-2 py-1 rounded text-sm"
                        />
                      </div>
                      <div>
                        <input
                          type="text"
                          value={editGameData.search_keywords || editGameData.searchKeywords || ''}
                          onChange={(e) => setEditGameData({...editGameData, search_keywords: e.target.value})}
                          placeholder="e.g. cakes, pies, flour, ovens"
                          className="w-full bg-purple-600 text-white px-2 py-1 rounded text-sm"
                        />
                        <p className="text-purple-300 text-xs mt-1">🔍 Separate search keywords with a comma</p>
                      </div>
                      
                      {/* 🆕 Parent Quiz Selection */}
                      <div>
                        <label className="text-purple-200 text-xs block mb-1">📁 Parent Quiz (for sub-sections):</label>
                        <select
                          value={editGameData.parent_id || ''}
                          onChange={(e) => setEditGameData({...editGameData, parent_id: e.target.value ? Number(e.target.value) : null})}
                          className="w-full bg-purple-600 text-white px-2 py-1 rounded text-sm"
                        >
                          <option value="">None (standalone quiz)</option>
                          {games.filter(g => !g.parent_id && g.game_id !== game.game_id).map(g => (
                            <option key={g.game_id} value={g.game_id}>{g.name}</option>
                          ))}
                        </select>
                      </div>
                      
                      {/* 🆕 Display Order */}
                      <div>
                        <label className="text-purple-200 text-xs block mb-1">🔢 Display Order:</label>
                        <input
                          type="number"
                          min="0"
                          value={editGameData.display_order || 0}
                          onChange={(e) => setEditGameData({...editGameData, display_order: Number(e.target.value)})}
                          className="w-full bg-purple-600 text-white px-2 py-1 rounded text-sm"
                        />
                      </div>
                      
                      <select
                        value={editGameData.groupQuizAdFrequency || 5}
                        onChange={(e) => setEditGameData({...editGameData, groupQuizAdFrequency: Number(e.target.value)})}
                        className="w-full bg-purple-600 text-white px-2 py-1 rounded text-sm"
                      >
                        <option value={0}>0 - No ads</option>
                        <option value={1}>1 - Every question</option>
                        <option value={3}>3 - Every 3 questions</option>
                        <option value={5}>5 - Every 5 questions</option>
                        <option value={10}>10 - Every 10 questions</option>
                        <option value={15}>15 - Every 15 questions</option>
                        <option value={20}>20 - Every 20 questions</option>
                      </select>
                      {/* 🔧 Display Mode dropdown removed - hardcoded to picture-on-black */}
                      
                      {/* 💬 Grid Message (per-game) */}
                      <div className="border-t border-purple-500 pt-2 mt-2">
                        <label className="text-purple-200 text-xs block mb-1">💬 Message on Grid (optional):</label>
                        <input
                          type="text"
                          value={editGameData.grid_message || ''}
                          onChange={(e) => setEditGameData({...editGameData, grid_message: e.target.value})}
                          placeholder="e.g. 'Sponsored by ACME Corp'"
                          className="w-full bg-purple-600 text-white px-2 py-1 rounded text-sm mb-2"
                        />
                        <p className="text-purple-300 text-xs mb-2">This message appears at the top of the picture grid for this quiz</p>
                      </div>
                      
                      {/* 📺 Advertisement URLs */}
                      <div className="border-t border-purple-500 pt-2 mt-2">
                        <p className="text-purple-200 text-xs mb-2 font-bold">📺 Advertisement URLs (per-quiz, overrides global)</p>
                        
                        <label className="text-purple-200 text-xs block mb-1">🖥️ Desktop Banner (728×90):</label>
                        <input
                          type="url"
                          value={editGameData.desktop_banner_url || ''}
                          onChange={(e) => setEditGameData({...editGameData, desktop_banner_url: e.target.value})}
                          placeholder="Leave blank to use global fallback"
                          className="w-full bg-purple-600 text-white px-2 py-1 rounded text-sm mb-2"
                        />
                        
                        <label className="text-purple-200 text-xs block mb-1">📱 Mobile Banner (320×50):</label>
                        <input
                          type="url"
                          value={editGameData.mobile_banner_url || ''}
                          onChange={(e) => setEditGameData({...editGameData, mobile_banner_url: e.target.value})}
                          placeholder="Leave blank to use global fallback"
                          className="w-full bg-purple-600 text-white px-2 py-1 rounded text-sm mb-2"
                        />
                        
                        <label className="text-purple-200 text-xs block mb-1">📺 Static Ad (300×250):</label>
                        <input
                          type="url"
                          value={editGameData.static_ad_url || ''}
                          onChange={(e) => setEditGameData({...editGameData, static_ad_url: e.target.value})}
                          placeholder="Leave blank to use global fallback"
                          className="w-full bg-purple-600 text-white px-2 py-1 rounded text-sm mb-2"
                        />
                        
                        <label className="text-purple-200 text-xs block mb-1">🚪 Exit Ad (320×480):</label>
                        <input
                          type="url"
                          value={editGameData.exit_ad_url || ''}
                          onChange={(e) => setEditGameData({...editGameData, exit_ad_url: e.target.value})}
                          placeholder="Leave blank to use global fallback"
                          className="w-full bg-purple-600 text-white px-2 py-1 rounded text-sm mb-1"
                        />
                        <div className="flex gap-1 mb-1">
                          <div className="w-16">
                            <label className="text-purple-300 text-xs">⏱️ Sec:</label>
                            <input
                              type="number"
                              min="0"
                              max="30"
                              value={editGameData.exitAdCountdown || 5}
                              onChange={(e) => setEditGameData({...editGameData, exitAdCountdown: Number(e.target.value)})}
                              className="w-full bg-purple-600 text-white px-1 py-1 rounded text-xs"
                            />
                          </div>
                          <div className="w-16">
                            <label className="text-purple-300 text-xs">👁️ Max:</label>
                            <input
                              type="number"
                              min="0"
                              value={editGameData.exitAdMaxImpressions || ''}
                              onChange={(e) => setEditGameData({...editGameData, exitAdMaxImpressions: e.target.value ? Number(e.target.value) : null})}
                              placeholder="∞"
                              className="w-full bg-purple-600 text-white px-1 py-1 rounded text-xs"
                            />
                          </div>
                          <div className="flex-1">
                            <label className="text-purple-300 text-xs">📅 Start:</label>
                            <input
                              type="date"
                              value={editGameData.exitAdStartDate || ''}
                              onChange={(e) => setEditGameData({...editGameData, exitAdStartDate: e.target.value})}
                              className="w-full bg-purple-600 text-white px-1 py-1 rounded text-xs"
                            />
                          </div>
                          <div className="flex-1">
                            <label className="text-purple-300 text-xs">📅 End:</label>
                            <input
                              type="date"
                              value={editGameData.exitAdEndDate || ''}
                              onChange={(e) => setEditGameData({...editGameData, exitAdEndDate: e.target.value})}
                              className="w-full bg-purple-600 text-white px-1 py-1 rounded text-xs"
                            />
                          </div>
                        </div>
                        
                        <label className="text-purple-200 text-xs block mb-1">🎬 Interstitial Ad (320×480):</label>
                        <input
                          type="url"
                          value={editGameData.interstitialAdUrl || ''}
                          onChange={(e) => setEditGameData({...editGameData, interstitialAdUrl: e.target.value})}
                          placeholder="https://via.placeholder.com/320x480"
                          className="w-full bg-purple-600 text-white px-2 py-1 rounded text-sm mb-2"
                        />
                        
                        <label className="text-purple-200 text-xs block mb-1">🔗 Click URL:</label>
                        <input
                          type="url"
                          value={editGameData.ad_click_url || ''}
                          onChange={(e) => setEditGameData({...editGameData, ad_click_url: e.target.value})}
                          placeholder="https://sponsor-website.com"
                          className="w-full bg-purple-600 text-white px-2 py-1 rounded text-sm"
                        />
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <button onClick={onSaveEditGame} className="flex-1 bg-green-600 text-white py-1 px-2 rounded text-xs">
                        <Check size={12} className="inline mr-1" />
                        Save
                      </button>
                      <button onClick={onCancelEditGame} className="flex-1 bg-red-600 text-white py-1 px-2 rounded text-xs">
                        <X size={12} className="inline mr-1" />
                        Cancel
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <h5 className="text-white font-bold">{game.name}</h5>
                        <p className="text-purple-200 text-xs">{game.description}</p>
                        {/* 🆕 Question Count */}
                        <p className="text-green-400 text-xs mt-1 font-bold">
                          📝 {questions?.filter(q => String(q.game_id) === String(game.game_id))?.length || 0} Questions
                          {' • '}
                          🖼️ {pictures?.filter(p => String(p.game_id) === String(game.game_id))?.length || 0} Pictures
                        </p>
                        <p className="text-purple-300 text-xs mt-1">
                          Group Quiz Ads: Every {game.group_quiz_ad_frequency || 5} questions
                        </p>
                        {game.grid_message && (
                          <p className="text-yellow-300 text-xs mt-1">💬 {game.grid_message}</p>
                        )}
                        {/* Picture Mode removed - always picture-on-black */}
                        {(game.desktop_banner_url || game.mobile_banner_url || game.static_ad_url || game.exit_ad_url) && (
                          <p className="text-blue-300 text-xs mt-1">
                            📺 Ads: {game.desktop_banner_url ? '🖥️' : ''}{game.mobile_banner_url ? '📱' : ''}{game.static_ad_url ? '📺' : ''}{game.exit_ad_url ? '🚪' : ''}
                          </p>
                        )}
                        {/* Exit Ad Stats */}
                        {game.exit_ad_url && (
                          <p className="text-yellow-300 text-xs mt-1">
                            🚪 Exit: {game.exitAdImpressions || 0}{game.exitAdMaxImpressions ? `/${game.exitAdMaxImpressions}` : ''} views
                            {game.exitAdMaxImpressions && game.exitAdImpressions >= game.exitAdMaxImpressions && (
                              <span className="text-red-400 ml-1">(LIMIT REACHED)</span>
                            )}
                          </p>
                        )}
                      </div>
                      <div className="flex items-center gap-3">
                        <label className="flex items-center gap-2 cursor-pointer group relative">
                          <input
                            type="checkbox"
                            checked={game.active}
                            onChange={() => {
                              // 🆕 2025.12.23: Validate before activating a game
                              if (!game.active) {
                                // Trying to ACTIVATE - check requirements
                                const gameQuestionCount = questions.filter(q => q.game_id === game.game_id).length;
                                const gamePictureCount = pictures.filter(p => p.game_id === game.game_id).length;
                                
                                if (gamePictureCount < 1 || gameQuestionCount < 4) {
                                  let errorMsg = '⚠️ Cannot activate this game!\n\nMinimum requirements:\n';
                                  errorMsg += `• Pictures: ${gamePictureCount}/1 ${gamePictureCount >= 1 ? '✅' : '❌'}\n`;
                                  errorMsg += `• Questions: ${gameQuestionCount}/4 ${gameQuestionCount >= 4 ? '✅' : '❌'}\n\n`;
                                  errorMsg += 'Please add the missing content before activating.';
                                  alert(errorMsg);
                                  return;
                                }
                              }
                              onToggleGameStatus(game);
                            }}
                            className="w-4 h-4"
                          />
                          <span className="text-xs text-white">{game.active ? 'Active' : 'Inactive'}</span>
                          <span className="hidden group-hover:block absolute bottom-full left-0 bg-black text-white text-xs p-2 rounded shadow-lg w-48 z-50">
                            💡 Active quizzes appear in player's quiz list. Inactive quizzes are hidden but not deleted.
                          </span>
                        </label>
                        <button
                          onClick={() => onSetFeaturedGame && onSetFeaturedGame(game)}
                          className={`px-2 py-1 rounded text-xs font-bold transition-all group relative ${
                            game.featured 
                              ? 'bg-yellow-500 text-black' 
                              : 'bg-gray-600 text-gray-300 hover:bg-gray-500'
                          }`}
                        >
                          ⭐ {game.featured ? 'Featured' : 'Set Featured'}
                          <span className="hidden group-hover:block absolute bottom-full right-0 bg-black text-white text-xs p-2 rounded shadow-lg w-56 z-50 font-normal text-left">
                            💡 Featured quiz appears prominently on Home page. Only one quiz can be featured at a time.
                          </span>
                        </button>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      {canEdit && (
                      <button
                        onClick={() => {
                          console.log('Edit button clicked, game:', game);
                          onEditGame && onEditGame(game);
                        }}
                        className="flex-1 bg-purple-600 hover:bg-purple-500 text-white text-xs py-2 rounded"
                      >
                        <Edit size={12} className="inline mr-1" />
                        Edit
                      </button>
                      )}
                      <button
                        onClick={() => {
                          setSelectedGameId(game.game_id);
                          setCurrentScreen('pictures');
                        }}
                        className="flex-1 bg-blue-600 hover:bg-blue-500 text-white text-xs py-2 rounded"
                      >
                        <Image size={12} className="inline mr-1" />
                        Pictures
                      </button>
                      <button
                        onClick={() => {
                          setSelectedGameId(game.game_id);
                          setCurrentScreen('questions');
                        }}
                        className="flex-1 bg-green-600 hover:bg-green-500 text-white text-xs py-2 rounded"
                      >
                        <FileText size={12} className="inline mr-1" />
                        Questions
                      </button>
                      {canEdit && (
                      <button
                        onClick={() => {
                          console.log('Delete button clicked for game:', game.name, game.game_id);
                          if (onDeleteGame) {
                            if (window.confirm(`Are you sure you want to delete "${game.name}"? This cannot be undone.`)) {
                              onDeleteGame(game.game_id);
                            }
                          } else {
                            console.error('onDeleteGame handler not provided');
                            alert('Delete function not available. Please check admin configuration.');
                          }
                        }}
                        className="flex-1 bg-red-600 hover:bg-red-500 text-white text-xs py-2 rounded"
                      >
                        <Trash2 size={12} className="inline mr-1" />
                        Delete
                      </button>
                      )}
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* 🆕 ENHANCED PICTURES SCREEN WITH DATE RANGES & IMPRESSION LIMITS */}
      {currentScreen === 'pictures' && (
        <div className="bg-purple-800 rounded-lg p-4 space-y-4">
          <div className="flex justify-between items-center">
            <h4 className="text-white font-bold">
              🖼️ Picture Library
              <InfoTooltip text="Upload up to 20 pictures per game. Pictures appear before questions in the Picture Grid. Set dates, impression limits, and display order. The first picture is the 'start picture' shown on the first grid of each session!" />
            </h4>
            <select
              value={selectedGameId || ''}
              onChange={(e) => setSelectedGameId(parseInt(e.target.value))}
              className="bg-purple-700 text-white px-3 py-2 rounded text-sm"
            >
              <option value="">Select game...</option>
              {games.map(game => (
                <option key={game.game_id} value={game.game_id}>{game.name}</option>
              ))}
            </select>
          </div>

          {!selectedGameId ? (
            <p className="text-purple-300 text-center py-8">Select a game to manage pictures</p>
          ) : (
            <>
              <div className="flex justify-between items-center">
                <p className="text-purple-200 text-sm">
                  {gamePictures.length}/20 pictures 
                  <span className="text-purple-400 text-xs ml-2">
                    ({gamePictures.filter(p => isPictureActive(p)).length} active)
                  </span>
                </p>
                {canEdit && (
                <label className={`${
                  gamePictures.length >= 20 
                    ? 'bg-purple-600 opacity-50 cursor-not-allowed' 
                    : 'bg-purple-600 hover:bg-purple-500 cursor-pointer'
                } text-white text-xs px-3 py-2 rounded flex items-center gap-1`}>
                  <Upload size={14} />
                  Upload Picture(s)
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    disabled={gamePictures.length >= 20}
                    onChange={(e) => handlePictureUpload(e, selectedGameId)}
                    className="hidden"
                  />
                </label>
                )}
              </div>

              {/* 🆕 FILE SIZE INFO */}
              <div className="bg-purple-700 rounded p-2">
                <p className="text-purple-200 text-xs">
                  📏 <strong>Max file size:</strong> 500KB per picture
                  <br />
                  📦 <strong>Bulk upload:</strong> Select multiple pictures at once
                  <br />
                  🔄 <strong>Rotation:</strong> Active pictures rotate automatically in Picture Grid
                </p>
              </div>

              {gamePictures.length === 0 ? (
                <p className="text-purple-300 text-center py-8">No pictures uploaded</p>
              ) : (
                <div className="space-y-3 max-h-96 overflow-y-auto">
                  {gamePictures.map((pic, idx) => {
                    const isActive = isPictureActive(pic);
                    const isEditing = editingPictureId === pic.pic_id;
                    
                    return (
                      <div key={pic.pic_id} className={`rounded p-3 ${
                        isActive ? 'bg-green-900 border-2 border-green-600' : 'bg-purple-700 border-2 border-purple-600'
                      }`}>
                        <div className="flex gap-3">
                          {/* Picture Preview */}
                          <img 
                            src={pic.image_url || pic.file_path} 
                            alt={pic.file_name}
                            className="w-24 h-24 object-cover rounded"
                          />
                          
                          {/* Picture Info */}
                          <div className="flex-1">
                            {isEditing ? (
                              <div className="space-y-2">
                                <input
                                  type="text"
                                  value={editingPictureData.file_name}
                                  onChange={(e) => setEditingPictureData({...editingPictureData, file_name: e.target.value})}
                                  placeholder="Picture name"
                                  className="w-full bg-purple-600 text-white px-2 py-1 rounded text-xs"
                                />
                                <div className="grid grid-cols-2 gap-2">
                                  <div>
                                    <label className="text-purple-200 text-xs block">Start Date:</label>
                                    <input
                                      type="date"
                                      value={editingPictureData.start_date || ''}
                                      onChange={(e) => setEditingPictureData({...editingPictureData, start_date: e.target.value || null})}
                                      className="w-full bg-purple-600 text-white px-2 py-1 rounded text-xs"
                                    />
                                  </div>
                                  <div>
                                    <label className="text-purple-200 text-xs block">End Date:</label>
                                    <input
                                      type="date"
                                      value={editingPictureData.end_date || ''}
                                      onChange={(e) => setEditingPictureData({...editingPictureData, end_date: e.target.value || null})}
                                      className="w-full bg-purple-600 text-white px-2 py-1 rounded text-xs"
                                    />
                                  </div>
                                </div>
                                <div>
                                  <label className="text-purple-200 text-xs block">Max Impressions:</label>
                                  <input
                                    type="number"
                                    value={editingPictureData.max_impressions || ''}
                                    onChange={(e) => setEditingPictureData({...editingPictureData, max_impressions: e.target.value ? parseInt(e.target.value) : null})}
                                    placeholder="Leave blank for unlimited"
                                    className="w-full bg-purple-600 text-white px-2 py-1 rounded text-xs"
                                  />
                                </div>
                                <div className="flex items-center gap-2 group relative">
                                  <input
                                    type="checkbox"
                                    checked={editingPictureData.active}
                                    onChange={(e) => setEditingPictureData({...editingPictureData, active: e.target.checked})}
                                    id={`active-${pic.pic_id}`}
                                  />
                                  <label htmlFor={`active-${pic.pic_id}`} className="text-white text-xs">Active</label>
                                  <span className="text-purple-400 text-xs ml-1" title="Active pictures appear in Picture Grid rotation during gameplay">ⓘ</span>
                                </div>
                                <div className="flex items-center gap-2 group relative">
                                  <input
                                    type="checkbox"
                                    checked={editingPictureData.featuredImage}
                                    onChange={(e) => setEditingPictureData({...editingPictureData, featuredImage: e.target.checked})}
                                    id={`featured-${pic.pic_id}`}
                                  />
                                  <label htmlFor={`featured-${pic.pic_id}`} className="text-yellow-300 text-xs">⭐ Featured Image</label>
                                  <span className="text-yellow-400 text-xs ml-1" title="Featured image shows as quiz thumbnail on Home page and quiz list">ⓘ</span>
                                </div>
                                <div className="flex gap-2">
                                  <button
                                    onClick={() => {
                                      onUpdatePicture(pic.pic_id, editingPictureData);
                                      setEditingPictureId(null);
                                      setEditingPictureData(null);
                                    }}
                                    className="flex-1 bg-green-600 hover:bg-green-500 text-white text-xs py-1 rounded"
                                  >
                                    <Check size={12} className="inline mr-1" />
                                    Save
                                  </button>
                                  <button
                                    onClick={() => {
                                      setEditingPictureId(null);
                                      setEditingPictureData(null);
                                    }}
                                    className="flex-1 bg-red-600 hover:bg-red-500 text-white text-xs py-1 rounded"
                                  >
                                    <X size={12} className="inline mr-1" />
                                    Cancel
                                  </button>
                                </div>
                              </div>
                            ) : (
                              <>
                                <div className="flex items-center gap-2 mb-1">
                                  <p className="text-white text-xs font-bold truncate">{pic.file_name}</p>
                                  {isActive && <span className="bg-green-600 text-white text-xs px-2 py-0.5 rounded">ACTIVE</span>}
                                  {!pic.active && <span className="bg-red-600 text-white text-xs px-2 py-0.5 rounded">DISABLED</span>}
                                </div>
                                <p className="text-purple-300 text-xs mb-1">
                                  Size: {(pic.file_size / 1024).toFixed(0)}KB
                                </p>
                                {pic.start_date && (
                                  <p className="text-purple-300 text-xs flex items-center gap-1">
                                    <Calendar size={10} />
                                    Start: {new Date(pic.start_date).toLocaleDateString()}
                                  </p>
                                )}
                                {pic.end_date && (
                                  <p className="text-purple-300 text-xs flex items-center gap-1">
                                    <Calendar size={10} />
                                    End: {new Date(pic.end_date).toLocaleDateString()}
                                  </p>
                                )}
                                {pic.max_impressions && (
                                  <p className="text-purple-300 text-xs flex items-center gap-1">
                                    <Eye size={10} />
                                    Impressions: {pic.current_impressions || 0} / {pic.max_impressions}
                                  </p>
                                )}
                                {!pic.start_date && !pic.end_date && !pic.max_impressions && (
                                  <p className="text-purple-400 text-xs italic">No restrictions (always active)</p>
                                )}
                              </>
                            )}
                          </div>
                        </div>
                        
                        {/* Action Buttons */}
                        {!isEditing && (
                          <div className="flex gap-1 mt-2">
                            <button
                              onClick={() => {
                                setEditingPictureId(pic.pic_id);
                                setEditingPictureData({...pic});
                              }}
                              className="flex-1 bg-purple-600 hover:bg-purple-500 text-white text-xs py-1 rounded"
                            >
                              <Edit size={12} className="inline mr-1" />
                              Edit
                            </button>
                            <button
                              onClick={() => onMovePicture(pic, 'up')}
                              disabled={idx === 0}
                              className="flex-1 bg-purple-600 text-white text-xs py-1 rounded disabled:opacity-30"
                            >
                              <ChevronUp size={12} className="inline" />
                            </button>
                            <button
                              onClick={() => onMovePicture(pic, 'down')}
                              disabled={idx === gamePictures.length - 1}
                              className="flex-1 bg-purple-600 text-white text-xs py-1 rounded disabled:opacity-30"
                            >
                              <ChevronDown size={12} className="inline" />
                            </button>
                            {canEdit && (
                            <button
                              onClick={() => onDeletePicture(pic)}
                              className="flex-1 bg-red-600 hover:bg-red-500 text-white text-xs py-1 rounded"
                            >
                              <Trash2 size={12} className="inline mr-1" />
                              Delete
                            </button>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* QUESTIONS SCREEN */}
      {currentScreen === 'questions' && (
        <div className="bg-purple-800 rounded-lg p-4 space-y-4">
          <div className="flex justify-between items-center">
            <h4 className="text-white font-bold">❓ Questions</h4>
            <select
              value={selectedGameId || ''}
              onChange={(e) => setSelectedGameId(parseInt(e.target.value))}
              className="bg-purple-700 text-white px-3 py-2 rounded text-sm"
            >
              <option value="">Select game...</option>
              {games.map(game => (
                <option key={game.game_id} value={game.game_id}>{game.name}</option>
              ))}
            </select>
          </div>

          {!selectedGameId ? (
            <p className="text-purple-300 text-center py-8">Select a game to manage questions</p>
          ) : (
            <>
              <div className="flex gap-2 mb-3">
                <select
                  value={questionFilter}
                  onChange={(e) => setQuestionFilter(e.target.value)}
                  className="bg-purple-700 text-white px-3 py-2 rounded text-sm"
                >
                  <option value="all">All ({gameQuestions.length})</option>
                  <option value="active">Active ({gameQuestions.filter(q => q.active).length})</option>
                  <option value="inactive">Inactive ({gameQuestions.filter(q => !q.active).length})</option>
                  <option value="reported">Reported ({gameQuestions.filter(q => q.reported).length})</option>
                </select>
                <div className="flex-1 relative">
                  <Search size={16} className="absolute left-2 top-2.5 text-purple-400" />
                  <input
                    type="text"
                    placeholder="Search questions..."
                    value={questionSearch}
                    onChange={(e) => setQuestionSearch(e.target.value)}
                    className="w-full bg-purple-700 text-white pl-8 pr-3 py-2 rounded text-sm"
                  />
                </div>
              </div>

              <p className="text-purple-200 text-sm">
                Showing {filteredQuestions.length} of {gameQuestions.length} questions
              </p>

              {/* 🆕 Delete All Questions Button */}
              {canEdit && gameQuestions.length > 0 && onDeleteAllQuestions && (
                <button
                  onClick={() => onDeleteAllQuestions(selectedGameId)}
                  className="w-full bg-red-700 hover:bg-red-600 text-white py-2 rounded text-sm font-bold flex items-center justify-center gap-2 border-2 border-red-500"
                >
                  <Trash2 size={16} />
                  🗑️ Delete All {gameQuestions.length} Questions
                </button>
              )}

              {filteredQuestions.length === 0 ? (
                <p className="text-purple-300 text-center py-8">No questions found</p>
              ) : (
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {filteredQuestions.map(question => (
                    <div key={question.question_id} className="bg-purple-700 rounded p-3">
                      {editingQuestionId === question.question_id ? (
                        <div className="space-y-2">
                          <textarea
                            value={editingQuestionData.question_text}
                            onChange={(e) => setEditingQuestionData({...editingQuestionData, question_text: e.target.value})}
                            className="w-full bg-purple-600 text-white px-2 py-1 rounded text-sm"
                            rows="2"
                          />
                          <input
                            type="text"
                            value={editingQuestionData.correct_answer}
                            onChange={(e) => setEditingQuestionData({...editingQuestionData, correct_answer: e.target.value})}
                            placeholder="Correct answer"
                            className="w-full bg-green-700 text-white px-2 py-1 rounded text-sm font-bold"
                          />
                          <input
                            type="text"
                            value={editingQuestionData.wrong_answer1}
                            onChange={(e) => setEditingQuestionData({...editingQuestionData, wrong_answer1: e.target.value})}
                            placeholder="Wrong answer 1"
                            className="w-full bg-purple-600 text-white px-2 py-1 rounded text-sm"
                          />
                          <input
                            type="text"
                            value={editingQuestionData.wrong_answer2}
                            onChange={(e) => setEditingQuestionData({...editingQuestionData, wrong_answer2: e.target.value})}
                            placeholder="Wrong answer 2"
                            className="w-full bg-purple-600 text-white px-2 py-1 rounded text-sm"
                          />
                          <input
                            type="text"
                            value={editingQuestionData.wrong_answer3}
                            onChange={(e) => setEditingQuestionData({...editingQuestionData, wrong_answer3: e.target.value})}
                            placeholder="Wrong answer 3"
                            className="w-full bg-purple-600 text-white px-2 py-1 rounded text-sm"
                          />
                          <input
                            type="number"
                            value={editingQuestionData.tag_value}
                            onChange={(e) => setEditingQuestionData({...editingQuestionData, tag_value: parseInt(e.target.value)})}
                            placeholder="Tag value"
                            className="w-full bg-purple-600 text-white px-2 py-1 rounded text-sm"
                          />
                          <div className="flex gap-2">
                            <button
                              onClick={() => {
                                console.log('📝 Saving question:', question.question_id);
                                console.log('📝 Data being sent:', editingQuestionData);
                                onEditQuestion(question.question_id, editingQuestionData);
                                setEditingQuestionId(null);
                                setEditingQuestionData(null);
                              }}
                              className="flex-1 bg-green-600 text-white py-1 rounded text-xs"
                            >
                              <Check size={12} className="inline mr-1" />
                              Save
                            </button>
                            <button
                              onClick={() => {
                                setEditingQuestionId(null);
                                setEditingQuestionData(null);
                              }}
                              className="flex-1 bg-red-600 text-white py-1 rounded text-xs"
                            >
                              <X size={12} className="inline mr-1" />
                              Cancel
                            </button>
                          </div>
                        </div>
                      ) : (
                        <>
                          <div className="flex justify-between items-start mb-2">
                            <div className="flex-1">
                              <p className="text-white text-sm mb-2">{question.question_text}</p>
                              <p className="text-green-300 text-xs">✓ {question.correct_answer}</p>
                              <p className="text-purple-300 text-xs">✗ {question.wrong_answer1}</p>
                              <p className="text-purple-300 text-xs">✗ {question.wrong_answer2}</p>
                              <p className="text-purple-300 text-xs">✗ {question.wrong_answer3}</p>
                              <p className="text-purple-400 text-xs mt-1">Tags: {question.tag_value}</p>
                            </div>
                            <div className="flex flex-col gap-1">
                              {question.reported && (
                                <span className="bg-yellow-600 text-white text-xs px-2 py-1 rounded">REPORTED</span>
                              )}
                              <label className="flex items-center gap-1 cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={question.active}
                                  onChange={() => onToggleQuestionStatus(question)}
                                />
                                <span className="text-xs text-white">{question.active ? 'Active' : 'Inactive'}</span>
                              </label>
                            </div>
                          </div>
                          {canEdit && (
                          <div className="flex gap-2">
                            <button
                              onClick={() => {
                                setEditingQuestionId(question.question_id);
                                setEditingQuestionData({...question});
                              }}
                              className="flex-1 bg-purple-600 hover:bg-purple-500 text-white text-xs py-1 rounded"
                            >
                              <Edit size={12} className="inline mr-1" />
                              Edit
                            </button>
                            <button
                              onClick={() => onDeleteQuestion(question)}
                              className="flex-1 bg-red-600 hover:bg-red-500 text-white text-xs py-1 rounded"
                            >
                              <Trash2 size={12} className="inline mr-1" />
                              Delete
                            </button>
                          </div>
                          )}
                        </>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* BULK UPLOAD SCREEN */}
      {currentScreen === 'bulk' && canEdit && (
        <div className="bg-purple-800 rounded-lg p-4 space-y-4">
          <h4 className="text-white font-bold">📤 Bulk Upload Questions</h4>
          
          <div className="bg-purple-700 rounded-lg p-3 space-y-2">
            <h5 className="text-white font-bold text-sm">Format Instructions:</h5>
            <p className="text-purple-200 text-xs">
              Each line should be: <code className="bg-purple-900 px-1 rounded">question;correct;wrong1;wrong2;wrong3</code>
            </p>
            <p className="text-purple-200 text-xs">
              Optional 6th field for explanation: <code className="bg-purple-900 px-1 rounded">question;correct;wrong1;wrong2;wrong3;explanation</code>
            </p>
            <p className="text-purple-300 text-xs">Example:</p>
            <code className="block bg-purple-900 text-purple-200 text-xs p-2 rounded overflow-x-auto">
              Who co-created Gavin & Stacey?;James Corden and Ruth Jones;Ricky Gervais;David Walliams;John Cleese
            </code>
          </div>

          <select
            value={selectedGameId || ''}
            onChange={(e) => {
              setSelectedGameId(parseInt(e.target.value));
              setValidationComplete(false);
            }}
            className="w-full bg-purple-700 text-white px-3 py-2 rounded text-sm"
          >
            <option value="">Select game to upload to...</option>
            {games.map(game => (
              <option key={game.game_id} value={game.game_id}>{game.name}</option>
            ))}
          </select>

          {!validationComplete ? (
            <>
              {/* 🆕 Skip Validation Toggle */}
              <div className="flex items-center justify-between bg-yellow-900 border border-yellow-600 rounded-lg p-3">
                <div>
                  <span className="text-yellow-200 font-bold text-sm">Skip Duplicate Checking</span>
                  <p className="text-yellow-300 text-xs">Disable similarity checks for bulk uploads with similar question text</p>
                </div>
                <button
                  onClick={() => setSkipValidation(!skipValidation)}
                  className={`px-4 py-2 rounded font-bold text-sm ${
                    skipValidation 
                      ? 'bg-red-600 hover:bg-red-700 text-white' 
                      : 'bg-green-600 hover:bg-green-700 text-white'
                  }`}
                >
                  {skipValidation ? '⚠️ CHECKS OFF' : '✅ CHECKS ON'}
                </button>
              </div>

              <div>
                <label className="text-white text-sm block mb-2">Paste Questions:</label>
                <textarea
                  value={bulkUploadText}
                  onChange={(e) => setBulkUploadText(e.target.value)}
                  placeholder="Paste your questions here..."
                  className="w-full bg-purple-700 text-white px-3 py-2 rounded text-sm font-mono"
                  rows="12"
                />
              </div>

              <div className="flex gap-2">
                <button
                  onClick={handleBulkUploadValidate}
                  disabled={!selectedGameId || !bulkUploadText.trim()}
                  className={`flex-1 ${
                    !selectedGameId || !bulkUploadText.trim() 
                      ? 'bg-purple-600 opacity-50 cursor-not-allowed' 
                      : 'bg-blue-600 hover:bg-blue-700'
                  } text-white py-2 rounded font-bold text-sm`}
                >
                  🔍 Validate Questions
                </button>
                <button
                  onClick={resetBulkUpload}
                  className="px-4 bg-gray-600 hover:bg-gray-700 text-white py-2 rounded text-sm"
                >
                  Clear
                </button>
              </div>
            </>
          ) : (
            <>
              {/* VALIDATION RESULTS */}
              <div className="space-y-4">
                
                {/* Format Errors - RED */}
                {bulkUploadErrors.length > 0 && (
                  <div className="bg-red-900 border-2 border-red-500 rounded-lg p-3 space-y-2">
                    <h5 className="text-white font-bold text-sm flex items-center gap-2">
                      <X size={16} className="text-red-400" />
                      ❌ Format Errors ({bulkUploadErrors.length}) - Must Fix
                    </h5>
                    <div className="max-h-32 overflow-y-auto space-y-1">
                      {bulkUploadErrors.map((error, idx) => (
                        <div key={idx} className="text-xs bg-red-800 p-2 rounded">
                          <p className="text-red-300 font-bold">Line {error.lineNumber}: {error.message}</p>
                          <p className="text-red-200 font-mono truncate">{error.line}</p>
                        </div>
                      ))}
                    </div>
                    <p className="text-red-300 text-xs">⚠️ Fix these errors in your source and re-paste</p>
                  </div>
                )}

                {/* Clean Questions - GREEN */}
                {parsedQuestions.length > 0 && (
                  <div className="bg-green-900 border-2 border-green-500 rounded-lg p-3">
                    <h5 className="text-white font-bold text-sm flex items-center gap-2">
                      <Check size={16} className="text-green-400" />
                      ✅ Ready to Upload ({parsedQuestions.length})
                    </h5>
                    <div className="max-h-32 overflow-y-auto mt-2 space-y-1">
                      {parsedQuestions.slice(0, 5).map((q, idx) => (
                        <p key={idx} className="text-green-200 text-xs truncate">• {q.question_text}</p>
                      ))}
                      {parsedQuestions.length > 5 && (
                        <p className="text-green-300 text-xs italic">...and {parsedQuestions.length - 5} more</p>
                      )}
                    </div>
                  </div>
                )}

                {/* Flagged Questions - YELLOW/ORANGE with Override */}
                {flaggedQuestions.length > 0 && (
                  <div className="bg-yellow-900 border-2 border-yellow-500 rounded-lg p-3 space-y-3">
                    <h5 className="text-white font-bold text-sm flex items-center gap-2">
                      <AlertTriangle size={16} className="text-yellow-400" />
                      ⚠️ Potential Duplicates ({flaggedQuestions.length}) - Review Required
                    </h5>
                    <p className="text-yellow-200 text-xs">These questions appear similar to existing ones. Check the box to override and include them anyway.</p>
                    
                    <div className="max-h-64 overflow-y-auto space-y-2">
                      {flaggedQuestions.map((flagged, idx) => (
                        <div key={idx} className={`p-3 rounded-lg ${overriddenFlags.has(flagged.lineNumber) ? 'bg-green-800 border border-green-500' : 'bg-yellow-800 border border-yellow-600'}`}>
                          <div className="flex items-start gap-3">
                            <input
                              type="checkbox"
                              checked={overriddenFlags.has(flagged.lineNumber)}
                              onChange={() => toggleOverride(flagged.lineNumber)}
                              className="mt-1 w-5 h-5 cursor-pointer"
                            />
                            <div className="flex-1">
                              <p className="text-white text-sm font-medium">{flagged.question_text}</p>
                              <div className="mt-2 space-y-1">
                                <p className="text-yellow-300 text-xs font-bold">Similar to:</p>
                                {flagged.similarTo.map((sim, sIdx) => (
                                  <p key={sIdx} className="text-yellow-200 text-xs pl-2 border-l-2 border-yellow-500">
                                    "{sim.question}" ({sim.similarity}% match)
                                  </p>
                                ))}
                              </div>
                              {overriddenFlags.has(flagged.lineNumber) && (
                                <p className="text-green-300 text-xs mt-2 font-bold">✓ Will be uploaded</p>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Summary & Action Buttons */}
                <div className="bg-purple-700 rounded-lg p-4 space-y-3">
                  <h5 className="text-white font-bold">📊 Upload Summary</h5>
                  <div className="grid grid-cols-3 gap-2 text-center">
                    <div className="bg-green-700 rounded p-2">
                      <p className="text-green-200 text-xs">Clean</p>
                      <p className="text-white font-bold text-lg">{parsedQuestions.length}</p>
                    </div>
                    <div className="bg-yellow-700 rounded p-2">
                      <p className="text-yellow-200 text-xs">Overridden</p>
                      <p className="text-white font-bold text-lg">{overriddenFlags.size}</p>
                    </div>
                    <div className="bg-red-700 rounded p-2">
                      <p className="text-red-200 text-xs">Errors</p>
                      <p className="text-white font-bold text-lg">{bulkUploadErrors.length}</p>
                    </div>
                  </div>

                  {bulkUploadErrors.length > 0 ? (
                    <div className="flex gap-2">
                      <button
                        onClick={resetBulkUpload}
                        className="flex-1 bg-purple-600 hover:bg-purple-500 text-white py-3 rounded font-bold text-sm"
                      >
                        ← Back to Edit
                      </button>
                    </div>
                  ) : (
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleBulkUploadConfirm(false)}
                        disabled={parsedQuestions.length === 0}
                        className={`flex-1 ${parsedQuestions.length === 0 ? 'bg-gray-600 cursor-not-allowed' : 'bg-green-600 hover:bg-green-700'} text-white py-3 rounded font-bold text-sm`}
                      >
                        Upload {parsedQuestions.length} Clean
                      </button>
                      {overriddenFlags.size > 0 && (
                        <button
                          onClick={() => handleBulkUploadConfirm(true)}
                          className="flex-1 bg-yellow-600 hover:bg-yellow-700 text-white py-3 rounded font-bold text-sm"
                        >
                          Upload All ({parsedQuestions.length + overriddenFlags.size})
                        </button>
                      )}
                      <button
                        onClick={resetBulkUpload}
                        className="px-4 bg-gray-600 hover:bg-gray-700 text-white py-3 rounded text-sm"
                      >
                        Cancel
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {/* 🆕 FIXED CHALLENGES SCREEN - 50 TRAKs (not tags) */}
      {currentScreen === 'challenges' && (
        <div className="bg-purple-800 rounded-lg p-4 space-y-4">
          <h4 className="text-white font-bold">🚩 Challenge Reports ({challenges.length})</h4>
          
          {challenges.length === 0 ? (
            <p className="text-purple-300 text-center py-8">No challenges submitted</p>
          ) : (
            <div className="space-y-3 max-h-96 overflow-y-auto">
              {challenges.map(challenge => (
                <div key={challenge.challenge_id} className="bg-purple-700 rounded p-3">
                  <div className="flex justify-between items-start mb-2">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h5 className="text-white font-bold text-sm">Challenge #{challenge.challenge_id}</h5>
                        <span className={`text-xs px-2 py-0.5 rounded ${
                          challenge.status === 'pending' ? 'bg-yellow-600' :
                          challenge.status === 'approved' ? 'bg-green-600' : 'bg-red-600'
                        } text-white`}>
                          {challenge.status.toUpperCase()}
                        </span>
                      </div>
                      <p className="text-purple-200 text-xs mb-1">Player: {challenge.player_name || 'Anonymous'}</p>
                      <p className="text-white text-xs mb-1"><strong>Q:</strong> {challenge.question_text}</p>
                      <p className="text-white text-xs mb-1"><strong>Correct Answer:</strong> {challenge.correct_answer}</p>
                      <p className="text-white text-xs mb-1"><strong>Player Answer:</strong> {challenge.player_answer}</p>
                      <p className="text-yellow-300 text-xs"><strong>Issue:</strong> {challenge.issue_type}</p>
                      {challenge.player_comments && (
                        <p className="text-purple-300 text-xs mt-1"><strong>Comments:</strong> {challenge.player_comments}</p>
                      )}
                    </div>
                  </div>
                  
                  {challenge.status === 'pending' && challengeButtonStates[challenge.challenge_id] !== 'sent' && (
                    <div className="mt-2 space-y-2">
                      <div className="bg-purple-600 rounded p-2 mb-2">
                        <p className="text-purple-200 text-xs mb-1">
                          <strong>Admin Response Template:</strong>
                        </p>
                        <p className="text-white text-xs">
                          Click Approve or Decline to load template (editable), then click SEND button
                        </p>
                      </div>
                      <textarea
                        placeholder="Click Approve or Decline button to load template response..."
                        className="w-full p-2 text-xs bg-purple-600 text-white rounded border-2 border-purple-500"
                        id={`admin-response-${challenge.challenge_id}`}
                        rows="3"
                      />
                      <div className="flex gap-2">
                        <button
                          onClick={async () => {
                            const responseField = document.getElementById(`admin-response-${challenge.challenge_id}`);
                            const buttonState = challengeButtonStates[challenge.challenge_id];
                            const cannedApproveResponse = `Thank you so much for reporting this question! You're absolutely right—that question was a bit off track. We really appreciate you taking the time to flag it; you're helping us make things better for everyone.\n\nWe've just added 50 TRAKs to your account as a thank-you for your help. Keep up the fantastic work!\n\nYou're not just playing the game, you're improving it. We appreciate you!`;
                            
                            // First click: Load template and change button
                            if (buttonState !== 'approve-ready') {
                              responseField.value = cannedApproveResponse;
                              responseField.focus();
                              setChallengeButtonStates(prev => ({...prev, [challenge.challenge_id]: 'approve-ready'}));
                              return;
                            }
                            
                            // Second click: Send to API
                            const response = responseField.value.trim();
                            if (!response) {
                              alert('Response cannot be empty');
                              return;
                            }
                            
                            // Disable buttons immediately
                            setChallengeButtonStates(prev => ({...prev, [challenge.challenge_id]: 'sending'}));
                            
                            try {
                              // Get admin token
                              const token = localStorage.getItem('admin_token');
                              
                              // 1. Update challenge status via API
                              console.log('🔵 Calling API to approve challenge:', challenge.challenge_id);
                              const updateRes = await fetch(`${API_URL}/admin/challenges/${challenge.challenge_id}`, {
                                method: 'PUT',
                                headers: {
                                  'Content-Type': 'application/json',
                                  'Authorization': `Bearer ${token}`
                                },
                                body: JSON.stringify({
                                  status: 'approved',
                                  admin_response: response,
                                  tags_awarded: 50
                                })
                              });
                              
                              if (!updateRes.ok) {
                                throw new Error('Failed to update challenge');
                              }
                              
                              // 2. Create claimable reward for player (50 TRAKs)
                              if (challenge.player_id) {
                                console.log('🎁 Creating reward for player:', challenge.player_id);
                                await fetch(`${API_URL}/admin/rewards`, {
                                  method: 'POST',
                                  headers: {
                                    'Content-Type': 'application/json',
                                    'Authorization': `Bearer ${token}`
                                  },
                                  body: JSON.stringify({
                                    player_id: challenge.player_id,
                                    reward_type: 'challenge_approved',
                                    traks_amount: 50,
                                    description: 'Challenge Approved - Thank you for your report!',
                                    milestone_name: 'Challenge Reward'
                                  })
                                });
                              }
                              
                              // 3. Also notify via local challenge prop if available
                              if (onApproveChallenge) {
                                onApproveChallenge(challenge, response);
                              }
                              
                              setChallengeButtonStates(prev => ({...prev, [challenge.challenge_id]: 'sent'}));
                              alert('✅ Challenge approved! 50 TRAKs reward created for player.');
                              console.log('🟢 Approval sent successfully!');
                              
                            } catch (err) {
                              console.error('❌ Error approving challenge:', err);
                              alert('Failed to approve challenge. Please try again.');
                              setChallengeButtonStates(prev => ({...prev, [challenge.challenge_id]: 'approve-ready'}));
                            }
                          }}
                          disabled={challengeButtonStates[challenge.challenge_id] === 'sending'}
                          className={`flex-1 ${
                            challengeButtonStates[challenge.challenge_id] === 'sending'
                              ? 'bg-gray-500 cursor-not-allowed'
                              : challengeButtonStates[challenge.challenge_id] === 'approve-ready' 
                                ? 'bg-red-600 hover:bg-red-700' 
                                : 'bg-green-600 hover:bg-green-700'
                          } text-white py-2 px-2 rounded text-xs font-bold`}
                        >
                          {challengeButtonStates[challenge.challenge_id] === 'sending'
                            ? '⏳ SENDING...'
                            : challengeButtonStates[challenge.challenge_id] === 'approve-ready' 
                              ? '📤 SEND APPROVE (+50 TRAKs)' 
                              : '✓ APPROVE (+50 TRAKs)'}
                        </button>
                        <button
                          onClick={async () => {
                            const responseField = document.getElementById(`admin-response-${challenge.challenge_id}`);
                            const buttonState = challengeButtonStates[challenge.challenge_id];
                            const cannedDeclineResponse = `Thank you so much for reporting this question! We really appreciate you taking the time to help us maintain quality and accuracy.\n\nAfter a careful review, our team has found that the question and its answer are correct. It might have been a tricky one!\n\nThe answer is correct as marked.\n\nEven though the question checked out this time, please don't stop letting us know if you spot something that seems wrong. Your vigilance is what keeps our content sharp.\n\nKeep up the great work, and happy learning!`;
                            
                            // First click: Load template and change button
                            if (buttonState !== 'decline-ready') {
                              responseField.value = cannedDeclineResponse;
                              responseField.focus();
                              setChallengeButtonStates(prev => ({...prev, [challenge.challenge_id]: 'decline-ready'}));
                              return;
                            }
                            
                            // Second click: Send to API
                            const response = responseField.value.trim();
                            if (!response) {
                              alert('Response cannot be empty');
                              return;
                            }
                            
                            // Disable buttons immediately
                            setChallengeButtonStates(prev => ({...prev, [challenge.challenge_id]: 'sending'}));
                            
                            try {
                              // Get admin token
                              const token = localStorage.getItem('admin_token');
                              
                              // Update challenge status via API
                              console.log('🟠 Calling API to decline challenge:', challenge.challenge_id);
                              const updateRes = await fetch(`${API_URL}/admin/challenges/${challenge.challenge_id}`, {
                                method: 'PUT',
                                headers: {
                                  'Content-Type': 'application/json',
                                  'Authorization': `Bearer ${token}`
                                },
                                body: JSON.stringify({
                                  status: 'declined',
                                  admin_response: response,
                                  tags_awarded: 0
                                })
                              });
                              
                              if (!updateRes.ok) {
                                throw new Error('Failed to update challenge');
                              }
                              
                              // Also notify via local challenge prop if available
                              if (onDeclineChallenge) {
                                onDeclineChallenge(challenge, response);
                              }
                              
                              setChallengeButtonStates(prev => ({...prev, [challenge.challenge_id]: 'sent'}));
                              alert('Challenge declined.');
                              console.log('🔴 Decline sent successfully!');
                              
                            } catch (err) {
                              console.error('❌ Error declining challenge:', err);
                              alert('Failed to decline challenge. Please try again.');
                              setChallengeButtonStates(prev => ({...prev, [challenge.challenge_id]: 'decline-ready'}));
                            }
                          }}
                          disabled={challengeButtonStates[challenge.challenge_id] === 'sending'}
                          className={`flex-1 ${
                            challengeButtonStates[challenge.challenge_id] === 'sending'
                              ? 'bg-gray-500 cursor-not-allowed'
                              : challengeButtonStates[challenge.challenge_id] === 'decline-ready' 
                                ? 'bg-red-600 hover:bg-red-700' 
                                : 'bg-orange-600 hover:bg-orange-700'
                          } text-white py-2 px-2 rounded text-xs font-bold`}
                        >
                          {challengeButtonStates[challenge.challenge_id] === 'sending'
                            ? '⏳ SENDING...'
                            : challengeButtonStates[challenge.challenge_id] === 'decline-ready' 
                              ? '📤 SEND DECLINE' 
                              : '✗ DECLINE'}
                        </button>
                      </div>
                    </div>
                  )}
                  
                  {/* Show "Response Sent" indicator when admin just responded */}
                  {challengeButtonStates[challenge.challenge_id] === 'sent' && challenge.status === 'pending' && (
                    <div className="mt-2 bg-green-700 rounded p-3 text-center">
                      <p className="text-white text-sm font-bold">✅ Response Sent!</p>
                      <p className="text-green-200 text-xs">Refresh to see updated status</p>
                    </div>
                  )}
                  
                  {challenge.status !== 'pending' && challenge.admin_response && (
                    <div className="mt-2 bg-purple-600 rounded p-2">
                      <p className="text-white text-xs"><strong>Admin Response:</strong> {challenge.admin_response}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* 🆕 ENHANCED MESSAGES SCREEN WITH BROADCAST & HIGHLIGHT */}
      {currentScreen === 'messages' && (
        <div className="bg-purple-800 rounded-lg p-4 space-y-4">
          <div className="flex justify-between items-center">
            <h4 className="text-white font-bold">💬 Messages</h4>
            <button
              onClick={() => setComposingMessage(!composingMessage)}
              className="bg-purple-600 hover:bg-purple-500 text-white text-xs px-3 py-2 rounded"
            >
              {composingMessage ? 'Cancel' : 'New Message'}
            </button>
          </div>

          {composingMessage && (
            <div className="bg-purple-700 rounded-lg p-4 space-y-3">
              <h5 className="text-white font-bold">Compose Message</h5>
              <div>
                <label className="text-purple-200 text-xs block mb-1">To:</label>
                <select
                  value={newMessageData.to}
                  onChange={(e) => setNewMessageData({...newMessageData, to: e.target.value})}
                  className="w-full bg-purple-600 text-white px-3 py-2 rounded text-sm"
                >
                  <option value="">-- Select Recipient --</option>
                  <option value="ALL_PLAYERS">📢 Broadcast to ALL Players</option>
                  <optgroup label="Individual Players">
                    {uniquePlayers.length === 0 ? (
                      <option disabled>No players have sent messages yet</option>
                    ) : (
                      uniquePlayers.map(player => (
                        <option key={player.playerId} value={player.name}>
                          {player.name} (ID: {player.playerId})
                        </option>
                      ))
                    )}
                  </optgroup>
                </select>
                {newMessageData.to === 'ALL_PLAYERS' && (
                  <p className="text-yellow-300 text-xs mt-1">
                    ⚠️ This will send a notification to ALL players!
                  </p>
                )}
              </div>
              <div>
                <label className="text-purple-200 text-xs block mb-1">Subject:</label>
                <input
                  type="text"
                  value={newMessageData.subject}
                  onChange={(e) => setNewMessageData({...newMessageData, subject: e.target.value})}
                  placeholder="Message subject..."
                  className="w-full bg-purple-600 text-white px-3 py-2 rounded text-sm"
                />
              </div>
              <div>
                <label className="text-purple-200 text-xs block mb-1">Message:</label>
                <textarea
                  value={newMessageData.message}
                  onChange={(e) => setNewMessageData({...newMessageData, message: e.target.value})}
                  placeholder="Type your message..."
                  className="w-full bg-purple-600 text-white px-3 py-2 rounded text-sm"
                  rows="4"
                />
              </div>
              <button
                onClick={async () => {
                  if (!newMessageData.to) {
                    alert('Please select a recipient');
                    return;
                  }
                  if (!newMessageData.subject.trim() || !newMessageData.message.trim()) {
                    alert('Please fill in subject and message');
                    return;
                  }
                  
                  // Call original handler
                  onSendMessage(newMessageData);
                  
                  // 📨 Also send notification via API
                  const token = localStorage.getItem('admin_token');
                  
                  if (newMessageData.to === 'ALL_PLAYERS') {
                    // Broadcast to all players - send to each unique player
                    console.log('📢 Broadcasting to all players...');
                    for (const player of uniquePlayers) {
                      if (player.playerId) {
                        try {
                          await fetch(`${API_URL}/admin/notifications`, {
                            method: 'POST',
                            headers: {
                              'Content-Type': 'application/json',
                              'Authorization': `Bearer ${token}`
                            },
                            body: JSON.stringify({
                              player_id: player.playerId,
                              type: 'admin_broadcast',
                              subject: newMessageData.subject,
                              message: newMessageData.message
                            })
                          });
                        } catch (err) {
                          console.error('❌ Failed to send to', player.name, err);
                        }
                      }
                    }
                    console.log(`📨 Broadcast sent to ${uniquePlayers.length} players`);
                  } else {
                    // Individual message - find the player ID
                    const selectedPlayer = uniquePlayers.find(p => p.name === newMessageData.to);
                    if (selectedPlayer && selectedPlayer.playerId) {
                      try {
                        await fetch(`${API_URL}/admin/notifications`, {
                          method: 'POST',
                          headers: {
                            'Content-Type': 'application/json',
                            'Authorization': `Bearer ${token}`
                          },
                          body: JSON.stringify({
                            player_id: selectedPlayer.playerId,
                            type: 'admin_message',
                            subject: newMessageData.subject,
                            message: newMessageData.message
                          })
                        });
                        console.log('📨 Notification sent to player via API');
                      } catch (err) {
                        console.error('❌ Failed to send notification:', err);
                      }
                    }
                  }
                  
                  setNewMessageData({ to: '', subject: '', message: '' });
                  setComposingMessage(false);
                  alert('Message sent!');
                }}
                className="w-full bg-green-600 hover:bg-green-700 text-white py-2 rounded font-bold text-sm"
              >
                Send Message
              </button>
            </div>
          )}

          <div className="space-y-2 max-h-96 overflow-y-auto">
            {displayMessages.length === 0 ? (
              <p className="text-purple-300 text-center py-8">No messages</p>
            ) : (
              displayMessages.slice().reverse().map(msg => (
                <div key={msg.id} className="bg-purple-700 rounded p-3">
                  <div className="flex items-start gap-2">
                    {/* 🆕 ENHANCED: Highlight icon for unread messages */}
                    {!msg.read && msg.type !== 'sent' && (
                      <span className="text-yellow-400 text-xl">💬</span>
                    )}
                    <div className="flex-1">
                      <div className="flex justify-between items-start mb-1">
                        <div>
                          <p className="text-white font-bold text-sm">{msg.subject}</p>
                          <p className="text-purple-300 text-xs">
                            {msg.type === 'sent' ? `To: ${msg.to}` : `From: ${msg.from} (Player ID: ${msg.playerId})`}
                            {msg.game && <span className="ml-2 text-purple-400">({msg.game})</span>}
                          </p>
                        </div>
                        <div className="flex gap-2 items-center">
                          {msg.replied && (
                            <span className="text-xs px-2 py-0.5 rounded bg-blue-600 text-white">
                              REPLIED
                            </span>
                          )}
                          <span className={`text-xs px-2 py-0.5 rounded ${
                            msg.type === 'sent' ? 'bg-blue-600' : 
                            msg.read ? 'bg-green-600' : 'bg-yellow-600'
                          } text-white`}>
                            {msg.type === 'sent' ? 'SENT' : msg.read ? 'READ' : 'NEW'}
                          </span>
                        </div>
                      </div>
                      <p className="text-white text-xs whitespace-pre-line">{msg.message}</p>
                      <p className="text-purple-400 text-xs mt-1">{new Date(msg.timestamp).toLocaleString()}</p>
                      
                      {/* 🆕 NEW: Reply button and reply area */}
                      {msg.type !== 'sent' && (
                        <div className="mt-2">
                          {replyingTo === msg.id ? (
                            <div className="bg-purple-800 p-3 rounded">
                              <textarea
                                value={replyText}
                                onChange={(e) => setReplyText(e.target.value)}
                                placeholder="Type your reply..."
                                className="w-full bg-purple-900 text-white p-2 rounded text-xs resize-none"
                                rows="3"
                              />
                              <div className="flex gap-2 mt-2">
                                <button
                                  onClick={() => handleSendReply(msg)}
                                  className="bg-blue-600 hover:bg-blue-500 text-white text-xs px-3 py-1 rounded"
                                >
                                  Send Reply
                                </button>
                                <button
                                  onClick={() => {
                                    setReplyingTo(null);
                                    setReplyText('');
                                  }}
                                  className="bg-gray-600 hover:bg-gray-500 text-white text-xs px-3 py-1 rounded"
                                >
                                  Cancel
                                </button>
                              </div>
                            </div>
                          ) : (
                            <button
                              onClick={() => setReplyingTo(msg.id)}
                              className="bg-blue-600 hover:bg-blue-500 text-white text-xs px-3 py-1 rounded flex items-center gap-1"
                            >
                              <Mail size={12} />
                              Reply to Player
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {/* 🆕 ENHANCED REWARDS SCREEN WITH DOCUMENTATION */}
      {currentScreen === 'rewards' && (
        <div className="bg-purple-800 rounded-lg p-4 space-y-4">
          <h4 className="text-white font-bold">🎁 Rewards System</h4>
          
          {/* 🆕 CLARIFICATION: What Rewards section is for */}
          <div className="bg-blue-900 border-2 border-blue-600 rounded-lg p-4 space-y-2">
            <h5 className="text-white font-bold flex items-center gap-2">
              <Gift size={16} />
              About Rewards
            </h5>
            <p className="text-blue-200 text-sm">
              The Rewards system allows you to manually award TRAKs or tags to players as bonuses, 
              gifts, or compensation. This is separate from automatic challenge approvals.
            </p>
            <p className="text-blue-300 text-xs mt-2">
              <strong>Use cases:</strong>
              <br />• Welcome bonuses for new players
              <br />• Compensation for technical issues
              <br />• Special event rewards
              <br />• Birthday or anniversary gifts
              <br />• Bug bounty rewards
            </p>
          </div>

          {players && players.length > 0 ? (
            <div className="space-y-3">
              <h5 className="text-white font-bold">Award Reward to Player</h5>
              <select
                className="w-full bg-purple-700 text-white px-3 py-2 rounded text-sm"
                onChange={(e) => {
                  const playerId = e.target.value;
                  if (!playerId) return;
                  const player = players.find(p => p.id === parseInt(playerId));
                  if (!player) return;
                  
                  const reward = prompt(`Award reward to ${player.name}:\n\nEnter number of TRAKs to award (positive number):`);
                  if (!reward || isNaN(reward) || parseInt(reward) <= 0) {
                    alert('Invalid reward amount');
                    return;
                  }
                  
                  const reason = prompt('Reason for reward (will be shown to player):');
                  if (!reason || !reason.trim()) {
                    alert('Reason is required');
                    return;
                  }
                  
                  onAwardReward(player, parseInt(reward), reason.trim());
                  e.target.value = '';
                }}
              >
                <option value="">Select player...</option>
                {players.map(player => (
                  <option key={player.id} value={player.id}>
                    {player.name} (TRAKs: {player.traks || 0})
                  </option>
                ))}
              </select>
            </div>
          ) : (
            <p className="text-purple-300 text-center py-8">No players available</p>
          )}

          {rewards && rewards.length > 0 && (
            <div className="space-y-2">
              <h5 className="text-white font-bold">Recent Rewards</h5>
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {rewards.slice().reverse().map(reward => (
                  <div key={reward.id} className="bg-purple-700 rounded p-3">
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="text-white font-bold text-sm">{reward.playerName}</p>
                        <p className="text-green-300 text-sm">+{reward.amount} TRAKs</p>
                        <p className="text-purple-300 text-xs">{reward.reason}</p>
                      </div>
                      <p className="text-purple-400 text-xs">{new Date(reward.timestamp).toLocaleDateString()}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* 🆕 CLEANED UP PICTURE GRID SCREEN */}
      {currentScreen === 'picturegrid' && (
        <div className="bg-purple-800 rounded-lg p-4 space-y-4">
          <h4 className="text-white font-bold text-lg">🎯 Picture Grid Settings</h4>
          
          {/* Enable/Disable for Games */}
          <div>
            <label className="text-white text-sm block mb-2">Enable Picture Grid for Games:</label>
            <div className="space-y-2 max-h-40 overflow-y-auto bg-purple-700 rounded p-2">
              {games.length === 0 ? (
                <p className="text-purple-300 text-xs">No games created yet</p>
              ) : (
                games.map(game => (
                  <label key={game.game_id} className="flex items-center gap-2 text-white text-sm">
                    <input
                      type="checkbox"
                      checked={(pictureGridSettings?.enabledGames || []).includes(game.game_id)}
                      onChange={() => {
                        const currentGames = pictureGridSettings?.enabledGames || [];
                        const updatedGames = currentGames.includes(game.game_id)
                          ? currentGames.filter(id => id !== game.game_id)
                          : [...currentGames, game.game_id];
                        onUpdatePictureGridSettings({
                          ...pictureGridSettings,
                          enabledGames: updatedGames
                        });
                      }}
                    />
                    <span>{game.name}</span>
                  </label>
                ))
              )}
            </div>
            <p className="text-purple-300 text-xs mt-1">
              ✓ When enabled: Picture grid appears for that game
              <br />
              ✗ When disabled: "No picture grid for this game" message shown to player
            </p>
          </div>
          
          {/* Picture Display Mode is configured per-game in Games Tab */}

          {/* Grid Message now per-game - removed from here */}
          <div className="bg-yellow-900 border border-yellow-600 rounded p-2">
            <p className="text-yellow-200 text-xs">
              💡 <strong>Grid Message</strong> is now per-quiz. Edit a quiz in the Games tab to set its message.
            </p>
          </div>

          {/* Instructions */}
          <div className="bg-blue-900 border-2 border-blue-600 rounded-lg p-4">
            <h5 className="text-white font-bold mb-2">📝 Picture Grid Instructions</h5>
            <p className="text-blue-200 text-sm mb-2">
              To add pictures to the Picture Grid:
            </p>
            <ol className="text-blue-300 text-xs space-y-1 list-decimal list-inside">
              <li>Go to the <strong>Pictures</strong> screen</li>
              <li>Select a game</li>
              <li>Upload up to 20 pictures per game</li>
              <li>Set date ranges and impression limits (optional)</li>
              <li>Active pictures will automatically rotate in the Picture Grid</li>
            </ol>
            <p className="text-blue-400 text-xs mt-3">
              💡 Pictures rotate through all active pictures for that game. Inactive, expired, 
              or impression-limited pictures are automatically skipped.
            </p>
          </div>

          {/* Link to Pictures Screen */}
          <button
            onClick={() => setCurrentScreen('pictures')}
            className="w-full bg-green-600 hover:bg-green-700 text-white py-3 rounded font-bold flex items-center justify-center gap-2"
          >
            <Image size={16} />
            Go to Pictures Screen to Upload
          </button>
        </div>
      )}
      
      {/* 📺 Banner Ad Display - Bottom of Game Admin */}
      {games && games.length > 0 && games.find(g => g.desktopBannerUrl) && (
        <div className="mt-6 border-t border-gray-700 pt-4">
          <p className="text-gray-400 text-xs text-center mb-2">Advertisement</p>
          {(() => {
            const gameWithAd = games.find(g => g.desktopBannerUrl);
            return gameWithAd ? (
              <a 
                href={gameWithAd.adClickUrl || 'https://traktag.com'} 
                target="_blank" 
                rel="noopener noreferrer"
                className="block"
              >
                <img 
                  src={gameWithAd.desktopBannerUrl} 
                  alt="Advertisement"
                  className="mx-auto max-w-full h-auto rounded"
                  style={{maxHeight: '90px'}}
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
              </a>
            ) : null;
          })()}
        </div>
      )}

      {/* APP SETTINGS SCREEN */}
      {currentScreen === 'appsettings' && (
        <div className="bg-purple-800 rounded-lg p-4 space-y-4">
          <h4 className="text-white font-bold text-lg">⚙️ App Settings</h4>
          
          {/* Legal Links */}
          <div className="bg-purple-700 rounded-lg p-4 space-y-3">
            <h5 className="text-white font-bold flex items-center gap-2">
              📋 Legal Pages
              <InfoTooltip text="These URLs appear as footer links on the Home screen. Create the pages on your website and enter the URLs here." />
            </h5>
            
            <div>
              <label className="text-purple-200 text-xs block mb-1">Terms & Conditions URL:</label>
              <input
                type="url"
                value={appSettings.termsUrl || ''}
                onChange={(e) => saveAppSettings({...appSettings, termsUrl: e.target.value})}
                placeholder="https://traktag.com/terms"
                className="w-full bg-purple-600 text-white px-3 py-2 rounded text-sm"
              />
            </div>
            
            <div>
              <label className="text-purple-200 text-xs block mb-1">Privacy Policy URL:</label>
              <input
                type="url"
                value={appSettings.privacyUrl || ''}
                onChange={(e) => saveAppSettings({...appSettings, privacyUrl: e.target.value})}
                placeholder="https://traktag.com/privacy"
                className="w-full bg-purple-600 text-white px-3 py-2 rounded text-sm"
              />
            </div>
            
            <div>
              <label className="text-purple-200 text-xs block mb-1">GDPR Policy URL:</label>
              <input
                type="url"
                value={appSettings.gdprUrl || ''}
                onChange={(e) => saveAppSettings({...appSettings, gdprUrl: e.target.value})}
                placeholder="https://traktag.com/gdpr"
                className="w-full bg-purple-600 text-white px-3 py-2 rounded text-sm"
              />
            </div>
          </div>
          
          {/* Contact Info */}
          <div className="bg-purple-700 rounded-lg p-4 space-y-3">
            <h5 className="text-white font-bold flex items-center gap-2">
              📧 Contact Information
            </h5>
            
            <div>
              <label className="text-purple-200 text-xs block mb-1">Support Email:</label>
              <input
                type="email"
                value={appSettings.supportEmail || ''}
                onChange={(e) => saveAppSettings({...appSettings, supportEmail: e.target.value})}
                placeholder="support@traktag.com"
                className="w-full bg-purple-600 text-white px-3 py-2 rounded text-sm"
              />
            </div>
          </div>
          
          {/* 🆕 2025.12.19: Testing Overrides */}
          <div className="bg-red-900 rounded-lg p-4 space-y-3 border-2 border-red-500">
            <h5 className="text-white font-bold flex items-center gap-2">
              🔧 Testing Overrides
              <span className="text-red-300 text-xs">(Remove before launch)</span>
            </h5>
            
            <div className="flex items-center justify-between">
              <div>
                <p className="text-white text-sm">Admin Direct Exit</p>
                <p className="text-red-300 text-xs">Bypass daily exit ad requirement</p>
              </div>
              <button
                onClick={() => {
                  const current = localStorage.getItem('adminDirectExit') === 'true';
                  localStorage.setItem('adminDirectExit', (!current).toString());
                  // Force re-render
                  saveAppSettings({...appSettings, _updated: Date.now()});
                }}
                className={`px-4 py-2 rounded font-bold text-sm ${
                  localStorage.getItem('adminDirectExit') === 'true'
                    ? 'bg-green-600 text-white'
                    : 'bg-gray-600 text-gray-300'
                }`}
              >
                {localStorage.getItem('adminDirectExit') === 'true' ? '✅ ON' : '❌ OFF'}
              </button>
            </div>
            
            <p className="text-red-400 text-xs">
              ⚠️ When ON, you can exit games without watching ads regardless of daily limit
            </p>
          </div>
          
          {/* Branding */}
          <div className="bg-purple-700 rounded-lg p-4 space-y-3">
            <h5 className="text-white font-bold flex items-center gap-2">
              🏷️ Branding
            </h5>
            
            <div>
              <label className="text-purple-200 text-xs block mb-1">App Name:</label>
              <input
                type="text"
                value={appSettings.appName || ''}
                onChange={(e) => saveAppSettings({...appSettings, appName: e.target.value})}
                placeholder="TRAKtag Quiz"
                className="w-full bg-purple-600 text-white px-3 py-2 rounded text-sm"
              />
            </div>
            
            <div>
              <label className="text-purple-200 text-xs block mb-1">Copyright Year:</label>
              <input
                type="text"
                value={appSettings.copyrightYear || ''}
                onChange={(e) => saveAppSettings({...appSettings, copyrightYear: e.target.value})}
                placeholder="2025"
                className="w-full bg-purple-600 text-white px-3 py-2 rounded text-sm"
                maxLength={4}
              />
            </div>
          </div>
          
          {/* Preview */}
          <div className="bg-gray-900 rounded-lg p-4">
            <h5 className="text-gray-400 text-xs font-bold mb-2">PREVIEW - Footer Links</h5>
            <div className="flex justify-center items-center gap-3 text-xs text-gray-500">
              <a href={appSettings.termsUrl} target="_blank" rel="noopener noreferrer" className="hover:text-purple-400 transition-colors">
                Terms & Conditions
              </a>
              <span>|</span>
              <a href={appSettings.privacyUrl} target="_blank" rel="noopener noreferrer" className="hover:text-purple-400 transition-colors">
                Privacy Policy
              </a>
              <span>|</span>
              <a href={appSettings.gdprUrl} target="_blank" rel="noopener noreferrer" className="hover:text-purple-400 transition-colors">
                GDPR
              </a>
            </div>
            <p className="text-gray-600 text-xs mt-2 text-center">© {appSettings.copyrightYear} {appSettings.appName}. All rights reserved.</p>
          </div>
          
          <p className="text-purple-300 text-xs">
            💡 Settings are saved automatically. Changes appear immediately on the Home screen footer.
          </p>
        </div>
      )}
      
      {/* ADMIN USERS SCREEN */}
      {currentScreen === 'adminusers' && (
        <div className="bg-purple-800 rounded-lg p-4 space-y-4">
          <div className="flex justify-between items-center">
            <h4 className="text-white font-bold">
              👥 Admin Users
              <InfoTooltip text="Manage admin accounts. Super Admins can add/edit other admins. Admins can manage content. Moderators have view-only access." />
            </h4>
            <button
              onClick={() => {
                const name = prompt('New admin name:');
                if (!name) return;
                const email = prompt('Admin email:');
                if (!email) return;
                const role = prompt('Role (super_admin / admin / moderator):', 'admin');
                if (!role) return;
                
                // Call API to create admin
                const token = localStorage.getItem('admin_token');
                fetch(`${API_URL}/admin/admins`, {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                  },
                  body: JSON.stringify({ name, email, role, password: 'TempPass123!' })
                })
                .then(res => res.json())
                .then(data => {
                  if (data.success) {
                    alert(`Admin created! Temporary password: TempPass123!\nPlease ask them to change it immediately.`);
                    // Refresh admin list
                  } else {
                    alert('Error: ' + (data.error || 'Failed to create admin'));
                  }
                })
                .catch(err => alert('Error creating admin: ' + err.message));
              }}
              className="bg-green-600 hover:bg-green-500 text-white text-xs px-3 py-2 rounded flex items-center gap-1"
            >
              <Plus size={14} />
              Add New Admin
            </button>
          </div>
          
          {/* Admin Roles Guide */}
          <div className="bg-purple-700/50 rounded-lg p-3">
            <p className="text-purple-200 text-xs font-bold mb-2">Role Permissions:</p>
            <div className="grid grid-cols-3 gap-2 text-xs">
              <div className="bg-red-600/30 rounded p-2">
                <p className="text-red-300 font-bold">🔴 Super Admin</p>
                <p className="text-red-200">Full access + manage admins</p>
              </div>
              <div className="bg-purple-600/30 rounded p-2">
                <p className="text-purple-300 font-bold">🟣 Admin</p>
                <p className="text-purple-200">Content, messages, challenges</p>
              </div>
              <div className="bg-blue-600/30 rounded p-2">
                <p className="text-blue-300 font-bold">🔵 Moderator</p>
                <p className="text-blue-200">View-only + reply messages</p>
              </div>
            </div>
          </div>
          
          {/* Admin List Placeholder - would be populated from API */}
          <div className="bg-purple-900 rounded-lg p-4">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-purple-300 border-b border-purple-700">
                  <th className="text-left py-2">Name</th>
                  <th className="text-left py-2">Email</th>
                  <th className="text-left py-2">Role</th>
                  <th className="text-left py-2">Last Login</th>
                  <th className="text-left py-2">Actions</th>
                </tr>
              </thead>
              <tbody className="text-white">
                <tr className="border-b border-purple-800">
                  <td className="py-2">Mark (You)</td>
                  <td className="py-2">mark@traktag.com</td>
                  <td className="py-2"><span className="bg-red-600 px-2 py-1 rounded text-xs">Super Admin</span></td>
                  <td className="py-2">Now</td>
                  <td className="py-2 text-gray-500">-</td>
                </tr>
              </tbody>
            </table>
            <p className="text-purple-400 text-xs mt-3 italic">
              💡 Admin list is fetched from the database. Add the /admin/admins endpoints to enable full functionality.
            </p>
          </div>
        </div>
      )}
      
      {/* STATISTICS SCREEN */}
      {currentScreen === 'statistics' && (
        <StatisticsDashboard games={games} />
      )}
    </div>
  );
}

// Stat Card Component
const StatCard = ({ title, value, subtitle, icon, color, loading }) => {
  const colorClasses = {
    blue: 'bg-blue-600/30 border-blue-500',
    green: 'bg-green-600/30 border-green-500',
    yellow: 'bg-yellow-600/30 border-yellow-500',
    teal: 'bg-teal-600/30 border-teal-500',
    purple: 'bg-purple-600/30 border-purple-500',
    orange: 'bg-orange-600/30 border-orange-500',
    amber: 'bg-amber-600/30 border-amber-500',
    red: 'bg-red-600/30 border-red-500'
  };
  
  return (
    <div className={`${colorClasses[color] || colorClasses.blue} border rounded-lg p-3`}>
      <div className="flex items-center justify-between mb-1">
        <span className="text-2xl">{icon}</span>
      </div>
      <p className={`text-white text-2xl font-bold ${loading ? 'animate-pulse' : ''}`}>
        {loading ? '...' : value}
      </p>
      <p className="text-white text-xs font-medium">{title}</p>
      <p className="text-gray-400 text-xs">{subtitle}</p>
    </div>
  );
};

// Statistics Dashboard Component with real API data
const StatisticsDashboard = ({ games }) => {
  const [stats, setStats] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [monthlySignups, setMonthlySignups] = useState([]);
  const [palStats, setPalStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  useEffect(() => {
    fetchAllStats();
  }, []);
  
  const fetchAllStats = async () => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      setError('Admin token not found. Please log in again.');
      setLoading(false);
      return;
    }
    
    const headers = {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    };
    
    try {
      // Fetch dashboard stats
      const dashboardRes = await fetch(`${API_URL}/admin/stats/dashboard`, { headers });
      if (dashboardRes.ok) {
        const dashboardData = await dashboardRes.json();
        if (dashboardData.success) {
          setStats(dashboardData.stats);
        }
      }
      
      // Fetch leaderboard
      const leaderboardRes = await fetch(`${API_URL}/admin/stats/leaderboard?limit=5`, { headers });
      if (leaderboardRes.ok) {
        const leaderboardData = await leaderboardRes.json();
        if (leaderboardData.success) {
          setLeaderboard(leaderboardData.players);
        }
      }
      
      // Fetch monthly signups
      const signupsRes = await fetch(`${API_URL}/admin/stats/signups`, { headers });
      if (signupsRes.ok) {
        const signupsData = await signupsRes.json();
        if (signupsData.success && signupsData.monthly) {
          setMonthlySignups(signupsData.monthly);
        }
      }
      
      // Fetch pal stats
      const palRes = await fetch(`${API_URL}/admin/stats/pal-requests`, { headers });
      if (palRes.ok) {
        const palData = await palRes.json();
        if (palData.success) {
          setPalStats(palData);
        }
      }
      
    } catch (err) {
      console.error('Failed to fetch stats:', err);
      setError('Failed to connect to server');
    }
    
    setLoading(false);
  };
  
  // Find max value for chart scaling
  const maxSignups = monthlySignups.length > 0 
    ? Math.max(...monthlySignups.map(m => parseInt(m.count))) 
    : 1;
  
  return (
    <div className="bg-purple-800 rounded-lg p-4 space-y-4">
      <div className="flex justify-between items-center">
        <h4 className="text-white font-bold flex items-center gap-2">
          📊 Platform Statistics
        </h4>
        <button
          onClick={fetchAllStats}
          className="bg-purple-600 hover:bg-purple-500 text-white text-xs px-3 py-2 rounded flex items-center gap-1"
        >
          🔄 Refresh
        </button>
      </div>
      
      {error && (
        <div className="bg-red-600/30 border border-red-500 rounded-lg p-3">
          <p className="text-red-300 text-sm">{error}</p>
        </div>
      )}
      
      {/* Key Metrics Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard 
          title="Total Players" 
          value={stats?.total_players?.toLocaleString() || '--'} 
          subtitle="Registered accounts"
          icon="👥"
          color="blue"
          loading={loading}
        />
        <StatCard 
          title="New Signups" 
          value={stats?.signups_this_month?.toLocaleString() || '--'} 
          subtitle="This month"
          icon="📈"
          color="green"
          loading={loading}
        />
        <StatCard 
          title="Pal Requests" 
          value={stats?.pal_requests_total?.toLocaleString() || '--'} 
          subtitle="Total sent"
          icon="🆘"
          color="yellow"
          loading={loading}
        />
        <StatCard 
          title="Pal Responses" 
          value={stats?.pal_responses_total?.toLocaleString() || '--'} 
          subtitle="Friends helped"
          icon="🤝"
          color="teal"
          loading={loading}
        />
      </div>
      
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard 
          title="Response Rate" 
          value={stats?.pal_response_rate ? `${stats.pal_response_rate}%` : '--'} 
          subtitle="Pal answers"
          icon="📊"
          color="purple"
          loading={loading}
        />
        <StatCard 
          title="TRAKs Earned" 
          value={stats?.total_traks_earned?.toLocaleString() || '--'} 
          subtitle="Total all time"
          icon="🏆"
          color="amber"
          loading={loading}
        />
        <StatCard 
          title="Active Today" 
          value={stats?.daily_active_users?.toLocaleString() || '--'} 
          subtitle="Daily active users"
          icon="🔥"
          color="red"
          loading={loading}
        />
        <StatCard 
          title="Games" 
          value={games?.length || 0} 
          subtitle="Quiz games"
          icon="🎮"
          color="orange"
          loading={false}
        />
      </div>
      
      {/* Monthly Signups Chart */}
      <div className="bg-purple-900 rounded-lg p-4">
        <h5 className="text-white font-bold mb-3">📅 Monthly Signups</h5>
        {monthlySignups.length > 0 ? (
          <div className="h-40 flex items-end justify-around gap-2">
            {monthlySignups.map((month, i) => (
              <div key={month.month_key} className="flex flex-col items-center flex-1">
                <div className="text-green-400 text-xs mb-1">{month.count}</div>
                <div 
                  className="bg-gradient-to-t from-green-600 to-green-400 rounded-t w-full max-w-12 transition-all" 
                  style={{ height: `${(parseInt(month.count) / maxSignups) * 100}%`, minHeight: '8px' }}
                ></div>
                <span className="text-purple-400 text-xs mt-2">{month.month}</span>
              </div>
            ))}
          </div>
        ) : (
          <div className="h-40 flex items-center justify-center">
            <p className="text-purple-400">{loading ? 'Loading...' : 'No signup data yet'}</p>
          </div>
        )}
      </div>
      
      {/* Ask a Pal Analytics */}
      {palStats && (
        <div className="bg-purple-900 rounded-lg p-4">
          <h5 className="text-white font-bold mb-3">🆘 Ask a Pal Analytics</h5>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="bg-purple-800 rounded-lg p-3 text-center">
              <p className="text-yellow-400 text-2xl font-bold">{palStats.total?.toLocaleString() || 0}</p>
              <p className="text-purple-300 text-xs">Total Requests</p>
            </div>
            <div className="bg-purple-800 rounded-lg p-3 text-center">
              <p className="text-green-400 text-2xl font-bold">{palStats.response_rate || 0}%</p>
              <p className="text-purple-300 text-xs">Response Rate</p>
            </div>
            <div className="bg-purple-800 rounded-lg p-3 text-center">
              <p className="text-teal-400 text-2xl font-bold">{palStats.signups_from_pal?.toLocaleString() || 0}</p>
              <p className="text-purple-300 text-xs">Signups from Pal</p>
            </div>
            <div className="bg-purple-800 rounded-lg p-3 text-center">
              <p className="text-blue-400 text-2xl font-bold">{palStats.this_month?.toLocaleString() || 0}</p>
              <p className="text-purple-300 text-xs">This Month</p>
            </div>
          </div>
        </div>
      )}
      
      {/* Top Performers */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-purple-900 rounded-lg p-4">
          <h5 className="text-white font-bold mb-3">🏅 Top Players (TRAKs)</h5>
          <div className="space-y-2">
            {leaderboard.length > 0 ? leaderboard.map((player, i) => (
              <div key={player.player_id} className="flex items-center justify-between bg-purple-800 rounded px-3 py-2">
                <div className="flex items-center gap-2">
                  <span className={`text-lg ${i < 3 ? 'text-yellow-400' : 'text-purple-400'}`}>
                    {i === 0 ? '🥇' : i === 1 ? '🥈' : i === 2 ? '🥉' : `#${i + 1}`}
                  </span>
                  <span className="text-white text-sm">{player.name}</span>
                </div>
                <span className="text-teal-400 font-bold">{player.total_traks?.toLocaleString()} TRAKs</span>
              </div>
            )) : (
              <p className="text-purple-400 text-sm text-center py-4">
                {loading ? 'Loading...' : 'No player data yet'}
              </p>
            )}
          </div>
        </div>
        
        <div className="bg-purple-900 rounded-lg p-4">
          <h5 className="text-white font-bold mb-3">🎮 Quiz Games</h5>
          <div className="space-y-2">
            {games?.slice(0, 5).map((game, i) => (
              <div key={game.game_id} className="flex items-center justify-between bg-purple-800 rounded px-3 py-2">
                <div className="flex items-center gap-2">
                  <span className={`text-lg ${i < 3 ? 'text-yellow-400' : 'text-purple-400'}`}>
                    {i === 0 ? '🥇' : i === 1 ? '🥈' : i === 2 ? '🥉' : `#${i + 1}`}
                  </span>
                  <span className="text-white text-sm">{game.name || game.game_name}</span>
                </div>
                <span className={`text-xs px-2 py-1 rounded ${game.active ? 'bg-green-600' : 'bg-gray-600'}`}>
                  {game.active ? 'Active' : 'Inactive'}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CompleteGameAdminPanel;
